import { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react'
import { Loader2 } from 'lucide-react'
import { useContextMenu } from '@papan/core-ui/ui'
import { sessionFetch, errorText } from '@papan/api-client'
import * as Y from 'yjs'
import { useYjs } from '../client/useYjs.js'
import { ForbiddenOverlay } from './ForbiddenOverlay.js'
import { FileStateBanner } from './FileStateBanner'
import { useSaveRefusalToast } from './useSaveRefusalToast.js'
import { useWorkbookRefusalToast } from './useWorkbookRefusalToast.js'

// Univer CSS
import '@univerjs/preset-sheets-core/lib/index.css'
import '@univerjs/preset-sheets-conditional-formatting/lib/index.css'
import '@univerjs/preset-sheets-drawing/lib/index.css'
import '@univerjs/preset-sheets-hyper-link/lib/index.css'
import '@univerjs/preset-sheets-note/lib/index.css'
import './office-fonts.css'

import type { EditorEditHandle, EditorUndoRedo } from '../client/editor-registry.js'
import { resolveChartData, parseRangeRef } from '@papan/charts/types'
import type { YDocChartEntry } from '@papan/charts/types'
import { renderChartToDataUrl } from '@papan/charts/ui'

/** Convert 0-based column index to A1 notation letter (0=A, 25=Z, 26=AA) */
function colToLetter(col: number): string {
  let s = ''
  let c = col
  while (c >= 0) {
    s = String.fromCharCode((c % 26) + 65) + s
    c = Math.floor(c / 26) - 1
  }
  return s
}

// Univer max dimensions (2^20 rows, 2^14 columns)
const MAX_ROWS = 1048576
const MAX_COLS = 16384

interface UniverModifierProps {
  /** Collab room name (required for edit mode, ignored in preview mode) */
  room: string
  fileId?: string
  fileName?: string
  onDirtyChange?: (dirty: boolean) => void
  onSaveRef?: React.MutableRefObject<(() => void) | null>
  onDiscardRef?: React.MutableRefObject<(() => void) | null>
  /** Register undo/redo with the app's Edit menu. Returns unregister fn. */
  registerUndoRedo?: (editor: EditorUndoRedo) => () => void
  /** 'edit' (default) = full collab editor, 'readonly' = read-only static view */
  mode?: 'edit' | 'readonly'
  /** @deprecated Use mode='readonly' instead */
  previewMode?: boolean
  /** Y.Doc state for readonly mode (from server preview endpoint) */
  previewData?: ArrayBuffer
  /** Generic view sync — viewSync[viewSyncId] = { scrollTop, activeSheet, ... } */
  viewSync?: import('./viewSync.js').ViewSyncRef
  viewSyncId?: string
  viewSyncTrigger?: number
  /** Callback fired when WebSocket connection state changes */
  onConnectedChange?: (connected: boolean) => void
  /** Callback fired when reload state changes (reconnect forced a fresh Y.Doc
   *  awaiting resync) — lifted so the wrapper toolbar shows "Reconnecting…". */
  onReloadingChange?: (reloading: boolean) => void
  /** Callback fired when the bounded reconnect gives up — lifted so the toolbar
   *  shows an honest "Disconnected — reload" instead of a perpetual spinner. */
  onGaveUpChange?: (gaveUp: boolean) => void
  onConflictChange?: (conflicted: boolean) => void
  onForceSaveRef?: React.MutableRefObject<(() => void) | null>
  isActive?: boolean
  /** Whether this file is part of a CSV workbook group (shows sheet bar) */
  isWorkbookGroup?: boolean
  /** Callback to request adding a sheet (workbook group). Opens the dialog in SpreadsheetEditor. */
  onWbAddSheetRequest?: (sheetCount: number, sheetNames: string[]) => void
}

// ─── Y.Doc Sheet Helpers (client-side, inline to avoid server import) ──

function getSheetOrder(doc: Y.Doc): string[] {
  return (doc.getMap('workbook').get('sheetOrder') as string[]) || []
}

function setSheetOrder(doc: Y.Doc, order: string[]): void {
  doc.getMap('workbook').set('sheetOrder', order)
}

function getSheetMaps(doc: Y.Doc, sheetId: string) {
  const sheet = doc.getMap('sheets').get(sheetId) as Y.Map<unknown> | undefined
  if (!sheet) return null

  // Lazily create sub-maps that may not exist on older Y.Docs
  function ensureMap(key: string): Y.Map<unknown> {
    let m = sheet!.get(key) as Y.Map<unknown> | undefined
    if (!m) {
      m = new Y.Map<unknown>()
      sheet!.set(key, m)
    }
    return m
  }

  return {
    sheet,
    cells: ensureMap('cells'),
    meta: ensureMap('meta'),
    styles: ensureMap('styles'),
    merges: ensureMap('merges'),
    formulas: ensureMap('formulas'),
    drawings: ensureMap('drawings'),
    cellImages: ensureMap('cellImages'),
    hyperlinks: ensureMap('hyperlinks'),
    notes: ensureMap('notes'),
    charts: ensureMap('charts'),
  }
}

// ─── Utility Functions ─────────────────────────────────────────────

/** Deep merge locale objects (shallow spread overwrites nested keys like `sheet`) */
function deepMergeLocales(...sources: Record<string, any>[]): Record<string, any> {
  const result: Record<string, any> = {}
  for (const src of sources) {
    for (const [key, val] of Object.entries(src)) {
      if (val && typeof val === 'object' && !Array.isArray(val) && result[key] && typeof result[key] === 'object') {
        result[key] = deepMergeLocales(result[key], val)
      } else {
        result[key] = val
      }
    }
  }
  return result
}

/** Serialize a value deterministically for cache comparison */
function stableStringify(obj: unknown): string {
  if (obj === null || obj === undefined) return ''
  if (typeof obj !== 'object') return JSON.stringify(obj)
  if (Array.isArray(obj)) return JSON.stringify(obj)
  const sorted: Record<string, unknown> = {}
  for (const key of Object.keys(obj as Record<string, unknown>).sort()) {
    sorted[key] = (obj as Record<string, unknown>)[key]
  }
  return JSON.stringify(sorted)
}

function cleanResolvedStyle(s: Record<string, any>): Record<string, any> {
  const result: Record<string, any> = {}
  for (const [key, val] of Object.entries(s)) {
    if (val === 0 || val === null || val === undefined || val === '' || val === false) continue
    if ((key === 'ul' || key === 'st') && typeof val === 'object' && val?.s === 0) continue
    if (key === 'cl' && typeof val === 'object' && val?.rgb) {
      const c = val.rgb.toLowerCase().replace(/\s/g, '')
      if (c === '#000000' || c === '#000' || c === 'black') continue
    }
    if (key === 'bg' && typeof val === 'object' && val?.rgb) {
      const b = val.rgb.toLowerCase().replace(/\s/g, '')
      if (b === '#ffffff' || b === '#fff' || b === 'white' || b === 'transparent') continue
    }
    result[key] = val
  }
  return result
}

function buildMenuConfig(fileName?: string, _isWorkbookGroup?: boolean): Record<string, { hidden: boolean }> {
  const ext = (fileName || '').split('.').pop()?.toLowerCase() || 'csv'
  if (ext === 'xlsx' || ext === 'xls') return {}

  const hidden = { hidden: true } as const
  const config: Record<string, { hidden: boolean }> = {
    'sheet.cf.menu.createConditionalFormatting': hidden,
    'sheet.cf.menu.manageConditionalFormatting': hidden,
    'sheet.cf.menu.clearRangeRules': hidden,
    'sheet.cf.menu.clearWorkSheetRules': hidden,
    'sheet.command.remove-all-data-validation': hidden,
    'sheet.command.remove-data-validation-rule': hidden,
    'sheets.command.clear-range-data-validation': hidden,
    'sheets.command.update-data-validation-options': hidden,
    'sheets.command.update-data-validation-setting': hidden,
    'sheetThreadComment.menu.commentManagement': hidden,
    'sheets.operation.show-comment-modal': hidden,
    'sheet.menu.print': hidden,
    'sheet.command.print': hidden,
  }

  // CSV: hide native sheet tab menu items — workbook groups use custom context menu,
  // standalone CSVs hide copy/remove entirely
  config['sheet.command.copy-sheet'] = hidden
  config['sheet.command.remove-sheet-confirm'] = hidden
  config['sheet.confirm.remove-sheet'] = hidden

  return config
}

function isCsvFile(fileName?: string): boolean {
  const ext = (fileName || '').split('.').pop()?.toLowerCase() || 'csv'
  return ext !== 'xlsx' && ext !== 'xls'
}

// ─── Sheet Tab Context Menu ──────────────────────────────────────
//
// Custom context menu for CSV workbook group sheet tabs.
// Replaces Univer's native sheet tab menu with workbook-aware actions.
// Labels follow Univer's resolved locale for consistency.

const sheetMenuLabels: Record<string, Record<string, string>> = {
  en: { rename: 'Rename', copy: 'Copy', relink: 'Relink...', unlink: 'Unlink', insert: 'Insert Sheet...', insertSep: 'separator' },
  'zh-CN': { rename: '重命名', copy: '复制', relink: '重新链接...', unlink: '取消链接', insert: '插入工作表...' },
  'zh-TW': { rename: '重新命名', copy: '複製', relink: '重新連結...', unlink: '取消連結', insert: '插入工作表...' },
}

function getSheetMenuLabel(appLang: string): Record<string, string> {
  return sheetMenuLabels[appLang] || sheetMenuLabels['en']
}

interface SheetTabMenuState {
  x: number
  y: number
  sheetId: string
  sheetName: string
}

/**
 * Find the sheet tab element and sheet name from a right-click target inside the Univer sheet bar.
 * Returns null if the click wasn't on a sheet tab.
 */
function resolveSheetTabFromTarget(target: HTMLElement): { sheetName: string } | null {
  // Walk up from target to find the tab container (has univer-cursor-pointer)
  let el: HTMLElement | null = target
  for (let i = 0; i < 8 && el; i++) {
    const cn = typeof el.className === 'string' ? el.className : ''
    if (cn.includes('univer-cursor-pointer') && cn.includes('univer-select-none')) {
      // Found the tab element — find the text inside it
      const textSpan = el.querySelector('.univer-truncate')
      const name = textSpan?.textContent?.trim()
      if (name) return { sheetName: name }
    }
    el = el.parentElement
  }
  return null
}

// ─── Drawing Sync Helpers ─────────────────────────────────────────

const EMU_PER_PIXEL = 9525

/** Convert Y.Doc drawing descriptor → Univer ISheetImage-compatible object */
function yDocDrawingToSheetImage(
  drawing: Record<string, any>,
  workbookId: string,
  sheetId: string,
  univerSheet?: any,
): any {
  const from = drawing.from || { row: 0, col: 0, rowOff: 0, colOff: 0 }
  const to = drawing.to
  // ext = displayed extent in EMUs (from XLSX), width/height = original image pixels (from sharp)
  // Use ext for display size; fall back to 200px default (NOT original image dimensions which can be huge)
  const widthPx = drawing.ext?.width ? drawing.ext.width / EMU_PER_PIXEL : 200
  const heightPx = drawing.ext?.height ? drawing.ext.height / EMU_PER_PIXEL : 200

  const sheetTransform: any = {
    from: {
      column: from.col,
      row: from.row,
      columnOffset: (from.colOff || 0) / EMU_PER_PIXEL,
      rowOffset: (from.rowOff || 0) / EMU_PER_PIXEL,
    },
    to: to ? {
      column: to.col,
      row: to.row,
      columnOffset: (to.colOff || 0) / EMU_PER_PIXEL,
      rowOffset: (to.rowOff || 0) / EMU_PER_PIXEL,
    } : {
      // Fallback: place bottom-right based on image pixel dimensions
      column: from.col,
      row: from.row,
      columnOffset: ((from.colOff || 0) / EMU_PER_PIXEL) + widthPx,
      rowOffset: ((from.rowOff || 0) / EMU_PER_PIXEL) + heightPx,
    },
  }

  const anchorMap: Record<string, string> = { twoCell: '1', absolute: '2' }

  // Detect imageSourceType: base64 data URLs need 'BASE64', others need 'URL'
  const source = drawing.imageUrl || ''
  const isBase64 = typeof source === 'string' && source.startsWith('data:')
  const imageSourceType = isBase64 ? 'BASE64' : 'URL'

  // Use stored full transform if available (has left/top for absolute positioning),
  // otherwise compute approximate pixel position from cell anchors using Univer defaults.
  // Note: XLSX-loaded images won't be pixel-perfect vs Excel (different rendering engines)
  // but position relative to cells is correct via sheetTransform.
  let transform: any
  if (drawing.transform) {
    transform = { ...drawing.transform }
  } else {
    // Query Univer's actual cell dimensions via the internal Worksheet API.
    // FWorksheet.getSheet() returns the Worksheet with getColumnWidth/getRowHeight.
    const ws = univerSheet?.getSheet?.()
    const getColW = (c: number): number => {
      try { return ws?.getColumnWidth?.(c) ?? 88 } catch { return 88 }
    }
    const getRowH = (r: number): number => {
      try { return ws?.getRowHeight?.(r) ?? 24 } catch { return 24 }
    }

    // Univer's drawing transform coordinates include the row header gutter
    // (row numbers column, default 46px) and column header gutter (A,B,C row, 20px).
    const ROW_HEADER_WIDTH = 46
    const COL_HEADER_HEIGHT = 20

    // Sum actual column widths up to from.col + row header gutter
    let left = ROW_HEADER_WIDTH
    for (let c = 0; c < from.col; c++) left += getColW(c)
    left += (from.colOff || 0) / EMU_PER_PIXEL

    // Sum actual row heights + column header gutter (no header row offset)
    let top = COL_HEADER_HEIGHT
    for (let r = 0; r < from.row; r++) top += getRowH(r)
    top += (from.rowOff || 0) / EMU_PER_PIXEL

    // Displayed size: prefer ext (from OOXML spPr, authoritative) over anchor-based approximation
    let displayWidth = widthPx
    let displayHeight = heightPx
    if (!(drawing.ext?.width && drawing.ext?.height) && to) {
      let toLeft = ROW_HEADER_WIDTH
      for (let c = 0; c < to.col; c++) toLeft += getColW(c)
      toLeft += (to.colOff || 0) / EMU_PER_PIXEL
      let toTop = COL_HEADER_HEIGHT
      for (let r = 0; r < to.row; r++) toTop += getRowH(r)
      toTop += (to.rowOff || 0) / EMU_PER_PIXEL
      const anchorWidth = toLeft - left
      const anchorHeight = toTop - top
      if (anchorWidth > 0) displayWidth = anchorWidth
      if (anchorHeight > 0) displayHeight = anchorHeight
    }

    // Position: from anchor = transform position (no srcRect reversal needed).
    // Size: from/to and ext give the VISIBLE (post-crop) size. Univer's transform
    // needs the UNCROPPED frame, so add srcRect offsets to width/height.
    const sr = drawing.srcRect as { left?: number; top?: number; right?: number; bottom?: number } | undefined
    transform = {
      left,
      top,
      width: displayWidth + (sr?.left || 0) + (sr?.right || 0),
      height: displayHeight + (sr?.top || 0) + (sr?.bottom || 0),
      angle: drawing.angle || 0,
    }
  }

  const result: any = {
    drawingId: drawing.drawingId,
    unitId: workbookId,
    subUnitId: sheetId,
    drawingType: 0, // DrawingTypeEnum.DRAWING_IMAGE
    imageSourceType,
    source,
    sheetTransform,
    axisAlignSheetTransform: sheetTransform,
    transform,
    anchorType: anchorMap[drawing.editAs] || '0',
  }
  // Restore crop rectangle if present
  if (drawing.srcRect) {
    result.srcRect = { ...drawing.srcRect }
  }
  return result
}

/**
 * Render Y.Doc chart entries as SVG images and insert them into Univer's drawing system.
 * Charts are rendered to SVG data URLs using Recharts, then inserted as float images
 * so they scroll/position correctly with the sheet.
 */
/** Build a map of sheet name → cells for cross-sheet ref resolution */
function buildSheetNameToCellsMap(doc: Y.Doc): Map<string, { cells: Y.Map<unknown> }> {
  const result = new Map<string, { cells: Y.Map<unknown> }>()
  const sheetsMap = doc.getMap('sheets')
  for (const sheetId of getSheetOrder(doc)) {
    const sheet = sheetsMap.get(sheetId) as Y.Map<unknown> | undefined
    if (!sheet) continue
    const name = sheet.get('name') as string | undefined
    if (!name) continue
    const cells = sheet.get('cells') as Y.Map<unknown>
    result.set(name, { cells })
  }
  return result
}

function renderChartsToSheet(
  chartsMap: Y.Map<unknown>,
  cellsMap: Y.Map<unknown>,
  workbookId: string,
  sheetId: string,
  univerSheet: any,
  sheetNameToCells?: Map<string, { cells: Y.Map<unknown> }>,
): any[] {
  const sheetImages: any[] = []

  chartsMap.forEach((v, _chartKey) => {
    const chart = v as YDocChartEntry
    if (chart?.type !== 'chart') return

    // Pre-load cells from all referenced sheets into a flat lookup.
    // resolveChartData → readRange strips the sheet name and calls getCellValue(row, col),
    // so we populate the lookup with values from the correct sheet for each ref.
    const cellLookup = new Map<string, string | number | null>()
    if (sheetNameToCells) {
      for (const ser of (chart.series || [])) {
        for (const ref of [ser.catRef, ser.valRef, ser.bubbleSizeRef].filter(Boolean)) {
          const range = parseRangeRef(ref!)
          if (!range) continue
          const refSheet = range.sheet ? sheetNameToCells.get(range.sheet) : null
          const refCells = refSheet ? refSheet.cells : cellsMap
          // Direct mapping — no header offset
          for (let r = range.start.row; r <= range.end.row; r++) {
            for (let c = range.start.col; c <= range.end.col; c++) {
              const val = refCells.get(`${r}:${c}`) as string | undefined
              if (val != null) cellLookup.set(`${r}:${c}`, val)
            }
          }
        }
      }
    }

    const getCellValue = (row: number, col: number): string | number | null => {
      if (row < 0) return null
      // Try pre-loaded cross-sheet lookup first (keyed by Excel 0-based row)
      const key = `${row}:${col}`
      const fromLookup = cellLookup.get(key)
      if (fromLookup !== undefined) {
        const val = String(fromLookup)
        const num = Number(val)
        return isNaN(num) ? val : num
      }
      // Fall back to current sheet — direct mapping
      const raw = cellsMap.get(`${row}:${col}`) as string | undefined
      if (raw == null) return null
      const val = String(raw)
      const num = Number(val)
      return isNaN(num) ? val : num
    }

    const config = resolveChartData(chart, getCellValue)
    if (config.data.length === 0 || config.series.length === 0) return

    // Compute pixel dimensions from anchor
    const anchor = chart.anchor
    const ws = univerSheet?.getSheet?.()
    const getColW = (c: number): number => {
      try { return ws?.getColumnWidth?.(c) ?? 88 } catch { return 88 }
    }
    const getRowH = (r: number): number => {
      try { return ws?.getRowHeight?.(r) ?? 24 } catch { return 24 }
    }

    const ROW_HEADER_WIDTH = 46
    const COL_HEADER_HEIGHT = 20

    let fromLeft = ROW_HEADER_WIDTH
    for (let c = 0; c < anchor.from.col; c++) fromLeft += getColW(c)
    fromLeft += (anchor.from.colOff || 0) / EMU_PER_PIXEL

    let fromTop = COL_HEADER_HEIGHT
    for (let r = 0; r < anchor.from.row; r++) fromTop += getRowH(r)
    fromTop += (anchor.from.rowOff || 0) / EMU_PER_PIXEL

    let toLeft = ROW_HEADER_WIDTH
    for (let c = 0; c < anchor.to.col; c++) toLeft += getColW(c)
    toLeft += (anchor.to.colOff || 0) / EMU_PER_PIXEL

    let toTop = COL_HEADER_HEIGHT
    for (let r = 0; r < anchor.to.row; r++) toTop += getRowH(r)
    toTop += (anchor.to.rowOff || 0) / EMU_PER_PIXEL

    const chartWidth = Math.max(200, toLeft - fromLeft)
    const chartHeight = Math.max(150, toTop - fromTop)

    // Render chart to SVG data URL
    config.width = Math.round(chartWidth)
    config.height = Math.round(chartHeight)
    const dataUrl = renderChartToDataUrl(config)

    // Build Univer image object using same structure as float drawings
    sheetImages.push({
      drawingId: `chart_${chart.chartId}`,
      unitId: workbookId,
      subUnitId: sheetId,
      drawingType: 0, // DRAWING_IMAGE
      imageSourceType: 'BASE64',
      source: dataUrl,
      sheetTransform: {
        from: {
          column: anchor.from.col,
          row: anchor.from.row,
          columnOffset: (anchor.from.colOff || 0) / EMU_PER_PIXEL,
          rowOffset: (anchor.from.rowOff || 0) / EMU_PER_PIXEL,
        },
        to: {
          column: anchor.to.col,
          row: anchor.to.row,
          columnOffset: (anchor.to.colOff || 0) / EMU_PER_PIXEL,
          rowOffset: (anchor.to.rowOff || 0) / EMU_PER_PIXEL,
        },
      },
      transform: {
        left: fromLeft,
        top: fromTop,
        width: chartWidth,
        height: chartHeight,
        angle: 0,
      },
      anchorType: '0', // twoCell
    })
  })

  // Reverse so that later charts (higher z-order in Excel) are inserted last → rendered on top
  return sheetImages.reverse()
}

/** Re-render chart images only if their SVG content actually changed (avoids flicker). */
function rerenderChartsIfChanged(
  api: any, yDoc: Y.Doc, sid: string,
  dataHashCache: Map<string, string>,
  applyingRemoteRef: { current: boolean },
  _undoRedoService?: any,
) {
  const wb = api.getActiveWorkbook()
  if (!wb) return
  const sheet = wb.getSheetBySheetId(sid)
  if (!sheet) return
  const s = getSheetMaps(yDoc, sid)
  if (!s?.charts || s.charts.size === 0) return

  // Build sheet name → cells map for cross-sheet ref resolution
  const sheetNameMap = buildSheetNameToCellsMap(yDoc)

  // Build data hash per chart from referenced cell values — track which changed
  const changedChartIds = new Set<string>()
  s.charts.forEach((v: unknown, chartId: string) => {
    const chart = v as any
    if (chart?.type !== 'chart') return
    const vals: string[] = []
    for (const ser of (chart.series || [])) {
      for (const ref of [ser.catRef, ser.valRef, ser.bubbleSizeRef].filter(Boolean)) {
        const range = parseRangeRef(ref)
        if (!range) continue
        // Resolve cells from the correct sheet
        const refSheet = range.sheet ? sheetNameMap.get(range.sheet) : null
        const refCells = refSheet ? refSheet.cells : s.cells
        // Direct mapping — no header offset
        const readCell = (r: number, c: number): string => {
          return String(refCells.get(`${r}:${c}`) ?? '')
        }
        if (range.start.col === range.end.col) {
          for (let r = range.start.row; r <= range.end.row; r++)
            vals.push(readCell(r, range.start.col))
        } else if (range.start.row === range.end.row) {
          for (let c = range.start.col; c <= range.end.col; c++)
            vals.push(readCell(range.start.row, c))
        } else {
          for (let r = range.start.row; r <= range.end.row; r++)
            for (let c = range.start.col; c <= range.end.col; c++)
              vals.push(readCell(r, c))
        }
      }
    }
    const dataHash = vals.join('|')
    const prev = dataHashCache.get(chartId)
    if (prev === undefined) {
      // First time seeing this chart — populate cache only, don't trigger re-render
      dataHashCache.set(chartId, dataHash)
    } else if (prev !== dataHash) {
      // Data actually changed — mark for re-render
      dataHashCache.set(chartId, dataHash)
      changedChartIds.add(chartId)
    }
  })

  if (changedChartIds.size === 0) return

  // Only re-render charts whose data actually changed
  const chartImages = renderChartsToSheet(s.charts, s.cells, wb.getId(), sid, sheet, sheetNameMap)
  const toUpdate = chartImages.filter((img: any) =>
    changedChartIds.has(img.drawingId.replace('chart_', ''))
  )
  if (toUpdate.length === 0) return

  // Record the undo entry that should receive the drawing mutations (the cell edit)
  const undoStack = _undoRedoService ? (_undoRedoService as any)._getUndoStackForFocused?.() : null
  const undoSizeBefore = undoStack?.length ?? 0
  const mergeTarget = undoStack?.[undoStack.length - 1]  // the cell edit entry

  applyingRemoteRef.current = true
  try {
    try {
      sheet.updateImages(toUpdate)
    } catch {
      for (const img of toUpdate) {
        try {
          const existing = sheet.getImageById(img.drawingId)
          if (existing) sheet.deleteImages([existing])
        } catch { /* */ }
      }
      try { sheet.insertImages(toUpdate) } catch { /* */ }
    }
  } finally {
    applyingRemoteRef.current = false
  }

  // Merge drawing undo entries into the cell edit entry so they undo together
  if (undoStack && mergeTarget && undoStack.length > undoSizeBefore) {
    while (undoStack.length > undoSizeBefore) {
      const drawingEntry = undoStack.pop()
      if (drawingEntry && mergeTarget) {
        // Prepend drawing undo mutations so they execute FIRST during undo
        mergeTarget.undoMutations.unshift(...drawingEntry.undoMutations)
        mergeTarget.redoMutations.push(...drawingEntry.redoMutations)
      }
    }
  }
}

/** Convert Univer drawing data (from ISheetDrawingService) → Y.Doc drawing descriptor */
function univerDrawingToYDoc(
  drawingData: any,
  drawingId: string,
  existingYDocEntry?: Record<string, any>,
): Record<string, unknown> {
  const st = drawingData.sheetTransform
  const anchorMap: Record<string, string> = { '1': 'twoCell', '2': 'absolute' }

  return {
    type: 'image',
    drawingId,
    // Preserve imageUrl/imageHash/mimeType from existing Y.Doc entry if available
    imageUrl: existingYDocEntry?.imageUrl || drawingData.source || '',
    imageHash: existingYDocEntry?.imageHash || '',
    mimeType: existingYDocEntry?.mimeType || '',
    width: existingYDocEntry?.width || 0,
    height: existingYDocEntry?.height || 0,
    from: st?.from ? {
      row: st.from.row,
      col: st.from.column,
      rowOff: Math.round((st.from.rowOffset || 0) * EMU_PER_PIXEL),
      colOff: Math.round((st.from.columnOffset || 0) * EMU_PER_PIXEL),
    } : undefined,
    to: st?.to ? {
      row: st.to.row,
      col: st.to.column,
      rowOff: Math.round((st.to.rowOffset || 0) * EMU_PER_PIXEL),
      colOff: Math.round((st.to.columnOffset || 0) * EMU_PER_PIXEL),
    } : undefined,
    ext: {
      width: Math.round((drawingData.transform?.width || 0) * EMU_PER_PIXEL),
      height: Math.round((drawingData.transform?.height || 0) * EMU_PER_PIXEL),
    },
    // Store full transform for accurate positioning on receiving side
    transform: drawingData.transform ? {
      left: drawingData.transform.left || 0,
      top: drawingData.transform.top || 0,
      width: drawingData.transform.width || 0,
      height: drawingData.transform.height || 0,
      angle: drawingData.transform.angle || 0,
      flipX: drawingData.transform.flipX || false,
      flipY: drawingData.transform.flipY || false,
      skewX: drawingData.transform.skewX || 0,
      skewY: drawingData.transform.skewY || 0,
    } : undefined,
    // Crop rectangle (percentage offsets from each edge, 0-1)
    srcRect: drawingData.srcRect ? {
      left: drawingData.srcRect.left || 0,
      top: drawingData.srcRect.top || 0,
      right: drawingData.srcRect.right || 0,
      bottom: drawingData.srcRect.bottom || 0,
    } : undefined,
    editAs: anchorMap[drawingData.anchorType] || 'oneCell',
  }
}

/** Build a Univer IStyleData object from our flat style record */
function buildUniverStyleData(style: Record<string, unknown>): Record<string, unknown> {
  const s: Record<string, unknown> = {}

  const bg = style.bg
  if (bg) {
    s.bg = typeof bg === 'object' && (bg as any)?.rgb ? bg : { rgb: bg as string }
  }

  const cl = style.cl
  s.cl = cl ? (typeof cl === 'object' && (cl as any)?.rgb ? cl : { rgb: cl as string }) : { rgb: '#000000' }

  s.bl = style.bl ? 1 : 0
  s.it = style.it ? 1 : 0
  s.fs = typeof style.fs === 'number' ? style.fs : 11
  if (style.ff) s.ff = style.ff

  const ul = style.ul
  const st = style.st
  s.ul = (ul && (ul === 1 || (typeof ul === 'object' && (ul as any)?.s === 1))) ? { s: 1 } : { s: 0 }
  s.st = (st && (st === 1 || (typeof st === 'object' && (st as any)?.s === 1))) ? { s: 1 } : { s: 0 }

  if (typeof style.ht === 'number') s.ht = style.ht
  if (typeof style.vt === 'number') s.vt = style.vt
  if (typeof style.tb === 'number') s.tb = style.tb
  if (style.n && typeof style.n === 'object' && (style.n as any).pattern) s.n = style.n
  if (style.tr && typeof style.tr === 'object' && typeof (style.tr as any).a === 'number') s.tr = style.tr

  return s
}

/** Apply full cell style for remote sync — sets all properties atomically via setValues */
function applyFullCellStyle(sheet: any, univerRow: number, col: number, style: Record<string, unknown>) {
  try {
    const range = sheet.getRange(univerRow, col)
    range.setValues([[{ s: buildUniverStyleData(style) }]])
  } catch { /* */ }
}

// ─── Build Univer Sheet Config from Y.Doc Sheet Maps ───────────────

function buildSheetConfig(
  sheetId: string,
  s: { sheet: Y.Map<unknown>; cells: Y.Map<unknown>; meta: Y.Map<unknown>; styles: Y.Map<unknown>; merges: Y.Map<unknown>; formulas: Y.Map<unknown> },
) {
  const rows = (s.meta.get('rows') as number) || 0
  const cols = (s.meta.get('cols') as number) || 0
  const sheetName = (s.sheet.get('name') as string) || 'Sheet'

  const cellData: Record<number, Record<number, any>> = {}

  // All rows are data — no header offset. Y.Doc row r = Univer row r.
  for (let r = 0; r < rows; r++) {
    cellData[r] = {}
    for (let c = 0; c < cols; c++) {
      const val = (s.cells.get(`${r}:${c}`) as string) || ''
      if (val !== '') {
        const num = Number(val)
        cellData[r][c] = { v: val.trim() !== '' && !isNaN(num) ? num : val }
      }
    }
  }

  // Merge persisted styles into cellData (direct mapping, no offset)
  for (const [key, style] of s.styles.entries()) {
    if (key.startsWith('__')) continue
    if (key.startsWith('h:')) continue  // Legacy header styles — skip
    const parts = key.split(':')
    const r = Number(parts[0])
    const c = Number(parts[1])
    if (!cellData[r]) cellData[r] = {}
    if (!cellData[r][c]) cellData[r][c] = { v: '' }
    cellData[r][c].s = style
  }

  // Inject formulas — keep cached .v from Y.Doc (server-side HyperFormula computed values)
  for (const [key, formula] of s.formulas.entries()) {
    const [r, c] = key.split(':').map(Number)
    if (!cellData[r]) cellData[r] = {}
    if (!cellData[r][c]) cellData[r][c] = {}
    cellData[r][c].f = formula as string
  }

  // Column data
  const columnData: Record<number, any> = {}
  for (const [key, val] of s.styles.entries()) {
    if (key.startsWith('__col:')) {
      columnData[Number(key.slice(6))] = val
    }
  }

  // Row data
  const rowData: Record<number, any> = {}
  for (const [key, val] of s.styles.entries()) {
    if (key.startsWith('__row:')) {
      rowData[Number(key.slice(6))] = val
    }
  }

  // Merge data (direct mapping, no offset)
  const mergeData: Array<{ startRow: number; endRow: number; startColumn: number; endColumn: number }> = []
  for (const [, mergeStr] of s.merges.entries()) {
    const [start, end] = (mergeStr as string).split('-')
    const [startR, startC] = start.split(':').map(Number)
    const [endR, endC] = end.split(':').map(Number)
    mergeData.push({
      startRow: startR, endRow: endR,
      startColumn: startC, endColumn: endC,
    })
  }

  const config: any = {
    id: sheetId,
    name: sheetName,
    rowCount: Math.max(rows, 100),
    columnCount: Math.max(cols, 26),
    cellData,
  }
  if (Object.keys(columnData).length > 0) config.columnData = columnData
  if (Object.keys(rowData).length > 0) config.rowData = rowData
  if (mergeData.length > 0) config.mergeData = mergeData

  return { config, cellData }
}

// ─── Populate Cache from Sheet Maps ────────────────────────────────

function populateCachesFromSheet(
  s: { cells: Y.Map<unknown>; styles: Y.Map<unknown>; formulas: Y.Map<unknown> },
  cellData: Record<number, Record<number, any>>,
  cellCache: Map<string, string>,
  styleCache: Map<string, string>,
  formulaCache: Map<string, string>,
) {
  cellCache.clear()
  for (const [key, val] of Object.entries(cellData)) {
    const r = Number(key)
    for (const [cKey, cell] of Object.entries(val as Record<number, any>)) {
      const c = Number(cKey)
      if (cell.v != null && cell.v !== '') cellCache.set(`${r}:${c}`, String(cell.v))
    }
  }

  styleCache.clear()
  for (const [key, style] of s.styles.entries()) {
    styleCache.set(key, stableStringify(style))
  }

  formulaCache.clear()
  for (const [key, val] of s.formulas.entries()) {
    formulaCache.set(key, val as string)
  }
}

// ─── Main Component ────────────────────────────────────────────────

export const UniverModifier = forwardRef<EditorEditHandle, UniverModifierProps>(function UniverModifier({ room, fileId, fileName, onDirtyChange, onSaveRef, onDiscardRef, registerUndoRedo, mode = 'edit', previewMode: legacyPreviewMode, previewData, onConnectedChange, onReloadingChange, onGaveUpChange, onConflictChange, onForceSaveRef, isActive, isWorkbookGroup, onWbAddSheetRequest, viewSync, viewSyncId, viewSyncTrigger: _viewSyncTrigger }, ref) {
  const isPreview = mode === 'readonly' || legacyPreviewMode
  const containerRef = useRef<HTMLDivElement>(null)
  const univerRef = useRef<{ univer: any; univerAPI: any } | null>(null)
  const [loading, setLoading] = useState(true)
  // The LIVE Univer instance, as state rather than only a ref, so the undo
  // registration below re-runs when it is rebuilt. A collab reconnect swaps
  // the Y.Doc and Univer is disposed and built again; keying that effect on
  // `loading` missed it entirely (loading never returns to true), leaving the
  // registration pointing at a destroyed instance — Ctrl+Z in the grid then
  // did nothing at all AND, because it still claimed the keystroke, did not
  // fall back to anything either.
  const [univerApi, setUniverApi] = useState<any>(null)
  const [error, setError] = useState<string | null>(null)
  const readyRef = useRef(false)
  const applyingRemoteRef = useRef(false)
  const needsRebaselineRef = useRef(true)  // Rebaseline caches on first command
  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Caches for the currently active sheet
  const cellCacheRef = useRef(new Map<string, string>())
  const styleCacheRef = useRef(new Map<string, string>())
  const formulaCacheRef = useRef(new Map<string, string>())
  // Track which sheet's Y.Maps we're currently observing
  const observedSheetIdRef = useRef<string | null>(null)
  const observerCleanupRef = useRef<(() => void) | null>(null)
  /**
   * Register undo/redo with the app's Edit menu.
   *
   * Its OWN effect, keyed on the prop, because the host takes the
   * registration away and gives it back as the user moves between panes —
   * and Univer's load is slow enough that "the tab is not on screen right
   * now" and "the spreadsheet is still coming up" overlap routinely. Keyed on
   * the live instance too, so a rebuild (a collab reconnect disposes Univer
   * and starts over) re-registers against the new one instead of holding a
   * handle to the old.
   */
  useEffect(() => {
    const api = univerApi
    if (!registerUndoRedo) return

    // BEFORE Univer is up, claim the keystroke anyway. It takes several
    // seconds to build, the host has already put the user in the editor
    // region, and an editor that registers nothing reads as a viewer — so
    // Ctrl+Z in those seconds left the tab and restored a file deleted
    // earlier. Does nothing, greys the menu out. (useYTextUndo does the same
    // for the text editors' startup window.)
    if (!api) {
      return registerUndoRedo({
        editable: true,
        undo: async () => false,
        redo: async () => false,
        onStateChange: (cb) => { cb({ canUndo: false, canRedo: false }); return () => {} },
      })
    }
    const svc = undoRedoServiceRef.current
    return registerUndoRedo({
      // Editable, so the keystroke stops here rather than walking past an
      // open spreadsheet into the drive.
      editable: true,
      undo: () => api.undo(),
      redo: () => api.redo(),
      // With the service we report Univer's real history. WITHOUT it we
      // cannot see that history at all, so we report "there is something" in
      // BOTH directions and let api.undo()/redo() decide — Univer keeps its
      // own stacks either way, and reporting them empty would leave the
      // keystroke stopping at an editor that could in fact have answered it.
      //
      // The cost is stated plainly: on this fallback path the Edit menu's
      // Undo and Redo are always enabled, including on a spreadsheet nobody
      // has touched, where choosing them does nothing. Optimistic in the
      // harmless direction; the alternative loses real undo.
      onStateChange: svc ? (cb) => {
        const sub = svc.undoRedoStatus$.subscribe((status: { undos: number; redos: number }) => {
          cb({ canUndo: status.undos > 0, canRedo: status.redos > 0 })
        })
        return () => sub.unsubscribe()
      } : (cb) => { cb({ canUndo: true, canRedo: true }); return () => {} },
    })
  }, [registerUndoRedo, univerApi])

  const undoRedoServiceRef = useRef<any>(null)
  const commandServiceRef = useRef<any>(null)
  const batchDisposeRef = useRef<any>(null)
  const dirtySheetIdsRef = useRef(new Set<string>())
  const drawingCleanupRef = useRef<(() => void) | null>(null)
  const selectionCleanupRef = useRef<(() => void) | null>(null)
  const sheetDrawingServiceRef = useRef<any>(null)
  const injectorRef = useRef<any>(null)
  const chartRerenderTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const chartDataHashCacheRef = useRef(new Map<string, string>())  // chartId → dataUrl for diffing

  // In preview mode, create a local Y.Doc from previewData instead of WebSocket
  const previewDocRef = useRef<Y.Doc | null>(null)
  const yjsResult = useYjs({ room: isPreview ? '' : room, active: isActive })
  // #823: moved to the bin / being deleted — the sheet stays readable (select, copy) but takes no more edits, which
  // the server would drop anyway
  // #1315 B: also while this editor's Remove Sheet is awaiting its answer (the server drops its updates meanwhile)
  const fileLocked = !isPreview && (!!yjsResult.fileState || yjsResult.removePending)
  const fileLockedRef = useRef(fileLocked)
  fileLockedRef.current = fileLocked
  useEffect(() => {
    if (!univerApi) return
    try { univerApi.getActiveWorkbook()?.setEditable(!fileLocked) } catch { /* no workbook yet */ }
  }, [univerApi, fileLocked])
  // Surface a REFUSED save (full drive, frozen tenant). The spreadsheet was one
  // of two `useYjs` consumers left unwired when this hook was added — and the
  // likeliest of them all to hit the ceiling, because a workbook save writes
  // every sheet as its own CSV through the version chokepoint.
  useSaveRefusalToast(yjsResult.saveError)
  // #1315 B: a refused / failed Add / Remove / Relink sheet — only the editor whose connection was answered toasts
  useWorkbookRefusalToast(yjsResult.wbError)
  const { doc: yjsDoc, connected: yjsConnected, synced: yjsSynced, isDirty: yjsIsDirty, isConflicted: yjsIsConflicted, bulkUpdating: yjsBulkUpdating, bulkUpdatingRef: yjsBulkUpdatingRef, forbidden: yjsForbidden, reloading: yjsReloading, save: yjsSave, discard: yjsDiscard, forceSave: yjsForceSave, sendWbAddSheet, sendWbRemoveSheet } = yjsResult

  // For preview mode, populate local Y.Doc from previewData
  useEffect(() => {
    if (!isPreview || !previewData) return
    const localDoc = new Y.Doc()
    Y.applyUpdate(localDoc, new Uint8Array(previewData))
    previewDocRef.current = localDoc
    return () => { localDoc.destroy() }
  }, [isPreview, previewData])

  const doc = isPreview ? (previewDocRef.current || new Y.Doc()) : yjsDoc
  const connected = isPreview ? true : yjsConnected
  useEffect(() => { onConnectedChange?.(connected) }, [connected, onConnectedChange])
  const reloading = isPreview ? false : yjsReloading
  useEffect(() => { onReloadingChange?.(reloading) }, [reloading, onReloadingChange])
  const gaveUp = isPreview ? false : yjsResult.gaveUp
  useEffect(() => { onGaveUpChange?.(gaveUp) }, [gaveUp, onGaveUpChange])

  // Listen for add-sheet events from SpreadsheetEditor dialog
  useEffect(() => {
    if (!isWorkbookGroup && !sendWbAddSheet) return
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (!detail) return
      // Only handle events from the tab that initiated the request
      if (detail.sourceFileId && detail.sourceFileId !== fileId) return
      sendWbAddSheet?.(detail)
    }
    window.addEventListener('papan:wb-add-sheet', handler)

    // Listen for remove-sheet confirmation
    const removeHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail || {}
      if (detail.sourceFileId && detail.sourceFileId !== fileId) return
      if (detail.sheetId) sendWbRemoveSheet?.(JSON.stringify({ sheetId: detail.sheetId, deleteFile: detail.deleteFile }))
    }
    window.addEventListener('papan:wb-remove-sheet', removeHandler)

    return () => {
      window.removeEventListener('papan:wb-add-sheet', handler)
      window.removeEventListener('papan:wb-remove-sheet', removeHandler)
    }
  }, [isWorkbookGroup, sendWbAddSheet, sendWbRemoveSheet])
  const isConflicted = isPreview ? false : yjsIsConflicted
  useEffect(() => { onConflictChange?.(isConflicted) }, [isConflicted, onConflictChange])
  useEffect(() => { if (onForceSaveRef) onForceSaveRef.current = yjsForceSave }, [yjsForceSave, onForceSaveRef])
  const synced = isPreview ? !!previewDocRef.current : yjsSynced
  const isDirty = isPreview ? false : yjsIsDirty
  const forbidden = isPreview ? false : yjsForbidden
  const bulkUpdating = isPreview ? false : yjsBulkUpdating
  const bulkUpdatingRef = isPreview ? { current: false } : yjsBulkUpdatingRef
  const save = isPreview ? (() => {}) : yjsSave
  const discard = isPreview ? (() => {}) : yjsDiscard

  useEffect(() => {
    if (isPreview) return
    onDirtyChange?.(isDirty)
    // Clear dirty sheet tracking when file becomes clean (save or discard)
    if (!isDirty) {
      dirtySheetIdsRef.current.clear()
    }
  }, [isDirty, onDirtyChange])

  // Bulk update complete (discard or agent edit) — rebuild Univer from Y.Doc.
  // bulkUpdatingRef was set true synchronously on MSG_BULK_UPDATE_START so
  // observers skip during Y.applyUpdate. This effect fires on the false transition.
  const prevBulkUpdatingRef = useRef(false)
  useEffect(() => {
    const wasBulkUpdating = prevBulkUpdatingRef.current
    prevBulkUpdatingRef.current = bulkUpdating
    // Only continue when transitioning true → false (bulk update just ended)
    if (bulkUpdating || !wasBulkUpdating) return

    const api = univerRef.current?.univerAPI
    const wb = api?.getActiveWorkbook()
    if (!wb) return

    const order = getSheetOrder(doc)
    if (order.length === 0) return

    const svc = undoRedoServiceRef.current

    // Batch all refresh operations into a single undo entry
    if (svc) {
      batchDisposeRef.current = svc.__tempBatchingUndoRedo(wb.getId())
    }

    // Process active sheet last so undo/redo stays on the user's current sheet
    const activeSheetId = wb.getActiveSheet()?.getSheetId()
    const sheetsToRefresh = [...order]
    if (activeSheetId) {
      const idx = sheetsToRefresh.indexOf(activeSheetId)
      if (idx !== -1) {
        sheetsToRefresh.splice(idx, 1)
        sheetsToRefresh.push(activeSheetId)
      }
    }

    // Float drawing rebuilds are deferred to after the undo batch
    const deferredDrawingRebuilds: Array<{
      sheet: any; sheetId: string; workbookId: string; drawings: Y.Map<unknown>
    }> = []

    applyingRemoteRef.current = true
    try {
      for (const sheetId of sheetsToRefresh) {
        const s = getSheetMaps(doc, sheetId)
        if (!s) continue
        const sheet = wb.getSheetBySheetId(sheetId)
        if (!sheet) continue

        // Sync sheet display name from Y.Doc (restores name on discard)
        const ydocName = s.sheet.get('name') as string | undefined
        if (ydocName && sheet.getSheetName() !== ydocName) {
          try { commandServiceRef.current?.syncExecuteCommand('sheet.mutation.set-worksheet-name', { subUnitId: sheetId, name: ydocName, unitId: wb.getId() }) } catch { /* */ }
        }

        const rows = (s.meta.get('rows') as number) || 0
        let cols = (s.meta.get('cols') as number) || 0

        // Cap cols to actual data extent — meta.cols can be 16384 (Excel max)
        if (cols > 500) {
          let actualMax = 0
          for (const key of s.cells.keys()) {
            const c = Number(key.split(':')[1])
            if (c + 1 > actualMax) actualMax = c + 1
          }
          if (actualMax > 0 && actualMax < cols) cols = actualMax
        }

        // Build a full matrix — no header offset, direct mapping.
        const matrix: any[][] = []

        // Collect styles into a lookup (direct mapping, no offset)
        const styleMap = new Map<string, Record<string, unknown>>()
        for (const [key, style] of s.styles.entries()) {
          if (key.startsWith('__') || key.startsWith('h:')) continue
          styleMap.set(key, style as Record<string, unknown>)
        }

        // All rows are data
        for (let r = 0; r < rows; r++) {
          const row: any[] = []
          for (let c = 0; c < cols; c++) {
            const key = `${r}:${c}`
            const formula = s.formulas.get(key) as string | undefined
            const val = (s.cells.get(key) as string) || ''
            const num = val !== '' ? Number(val) : NaN
            const cellVal = val === '' ? null : (!isNaN(num) ? num : val)
            const style = styleMap.get(key)

            const cell: any = formula
              ? { v: cellVal, f: formula, s: null }
              : (cellVal != null ? { v: cellVal, s: null } : { v: null, s: null })

            if (style) {
              cell.s = buildUniverStyleData(style)
            }
            row.push(cell)
          }
          matrix.push(row)
        }

        // Clear existing rich-text cells before setValues (hyperlinks and cell
        // images live in `p` which setValues doesn't overwrite).
        //
        // For cell images: a pre-existing image that will be re-inserted from
        // Y.Doc is a no-op (clear + re-insert = same state), so we skip undo/redo
        // for those cells entirely. Only push undo/redo for images being REMOVED
        // (present in Univer but not in Y.Doc after revert).
        const preExistingCellImageKeys = new Set<string>()
        const yDocCellImageKeys = new Set<string>()
        if (s.cellImages) {
          for (const [, entry] of s.cellImages.entries()) {
            const ci = entry as { row: number; col: number }
            if (ci) yDocCellImageKeys.add(`${ci.row}:${ci.col}`)
          }
        }
        if (!isCsvFile(fileName) && commandServiceRef.current) {
          const dataRange = sheet.getDataRange?.()
          const richCellDatas: (any | null)[][] = dataRange?.getCellDatas?.() || []
          const wbId = wb.getId()
          for (let r = 0; r < richCellDatas.length; r++) {
            const row = richCellDatas[r]
            if (!row) continue
            for (let c = 0; c < row.length; c++) {
              const cell = row[c]
              if (!cell?.p) continue
              const hasHyperlinks = cell.p.body?.customRanges?.some((cr: any) => cr.rangeType === 0)
              const hasCellImage = cell.p.drawingsOrder?.length > 0
              if (hasCellImage) preExistingCellImageKeys.add(`${r}:${c}`)
              // Skip undo/redo for cell images that will be re-inserted (no-op)
              const willBeReinserted = hasCellImage && yDocCellImageKeys.has(`${r}:${c}`)
              if (hasHyperlinks || hasCellImage) {
                const oldCell = { ...cell }
                const redoParams = { unitId: wbId, subUnitId: sheetId, cellValue: { [r]: { [c]: null } } }
                const undoParams = { unitId: wbId, subUnitId: sheetId, cellValue: { [r]: { [c]: oldCell } } }
                if (commandServiceRef.current.syncExecuteCommand('sheet.mutation.set-range-values', redoParams)) {
                  // Only track undo/redo for cells NOT being re-inserted as cell images
                  if (!willBeReinserted) {
                    svc?.pushUndoRedo({
                      unitID: wbId,
                      redoMutations: [{ id: 'sheet.mutation.set-range-values', params: redoParams }],
                      undoMutations: [{ id: 'sheet.mutation.set-range-values', params: undoParams }],
                    })
                  }
                }
              }
            }
          }
        }

        // Clear formats on the used range only (not entire sheet — that takes 3s+)
        try {
          const dr = sheet.getDataRange?.()
          if (dr) {
            dr.clearFormat()
          }
        } catch { /* */ }

        // Single setValues call for the entire sheet (values + styles)
        if (cols > 0 && rows > 0) {
          sheet.getRange(0, 0, rows, cols).setValues(matrix)
        }

        // Apply column widths and row heights
        for (const [key, val] of s.styles.entries()) {
          const colData = val as Record<string, any>
          if (key.startsWith('__col:') && colData?.w != null) {
            const colIdx = Number(key.slice(6))
            if (colIdx < cols) {
              try { sheet.setColumnWidth(colIdx, colData.w) } catch { /* */ }
            }
          } else if (key.startsWith('__row:') && colData?.h != null) {
            const rowIdx = Number(key.slice(6))
            if (rowIdx < rows) {
              try { sheet.setRowHeight(rowIdx, colData.h) } catch { /* */ }
            }
          }
        }

        // Collect float drawing rebuild info — actual delete/insert is deferred
        // to after the undo batch. Drawing COMMAND APIs work correctly on their
        // own but their JSON1 ops fail when replayed inside a large batched undo
        // (Univer's lm executor short-circuits on first failure). So we execute
        // them after the batch and merge the resulting undo entries manually.
        if (s.drawings && !isCsvFile(fileName)) {
          deferredDrawingRebuilds.push({ sheet, sheetId, workbookId: wb.getId(), drawings: s.drawings })
        }

        // Rebuild cell images, notes, hyperlinks inside the undo batch so they
        // group with cell changes and Ctrl+Z undoes everything together.
        if (!isCsvFile(fileName)) {
          const cmdSvc = commandServiceRef.current
          const unitId = wb.getId()

          // Re-insert cell images from Y.Doc with undo/redo tracking.
          // Pre-existing images (in Univer before discard AND still in Y.Doc)
          // are a no-op (cleared then re-inserted) — skip undo/redo for both
          // steps so undo/redo doesn't accidentally remove them.
          if (s.cellImages && cmdSvc) {
            for (const [, entry] of s.cellImages.entries()) {
              const ci = entry as { row: number; col: number; p: any }
              if (!ci?.p) continue
              try {
                const univerRow = ci.row
                const cellData = { p: ci.p, t: 1, s: { vt: 1 } }
                const redoParams = { unitId, subUnitId: sheetId, cellValue: { [univerRow]: { [ci.col]: cellData } } }
                if (cmdSvc.syncExecuteCommand('sheet.mutation.set-range-values', redoParams)) {
                  // Only push undo for NEW cell images (not pre-existing ones)
                  if (!preExistingCellImageKeys.has(`${univerRow}:${ci.col}`)) {
                    const undoParams = { unitId, subUnitId: sheetId, cellValue: { [univerRow]: { [ci.col]: null } } }
                    svc?.pushUndoRedo({
                      unitID: unitId,
                      redoMutations: [{ id: 'sheet.mutation.set-range-values', params: redoParams }],
                      undoMutations: [{ id: 'sheet.mutation.set-range-values', params: undoParams }],
                    })
                  }
                }
              } catch { /* */ }
            }
          }

          // Re-insert hyperlinks from Y.Doc (cell p:null in setValues above
          // already cleared stale rich text / hyperlink custom ranges)
          if (s.hyperlinks) {
            for (const [, linkObj] of s.hyperlinks.entries()) {
              const link = linkObj as { row: number; col: number; payload: string; display?: string }
              try {
                sheet.getRange(link.row, link.col).setHyperLink(link.payload, link.display)
              } catch { /* */ }
            }
          }

          // Clear existing notes with undo/redo support
          try {
            const existingNotes = sheet.getNotes?.()
            if (existingNotes?.length > 0 && cmdSvc) {
              for (const n of existingNotes) {
                const redoParams = { unitId, sheetId, noteId: n.id, row: n.row, col: n.col }
                const undoParams = { unitId, sheetId, row: n.row, col: n.col, note: { ...n } }
                try {
                  if (cmdSvc.syncExecuteCommand('sheet.mutation.remove-note', redoParams)) {
                    svc?.pushUndoRedo({
                      unitID: unitId,
                      redoMutations: [{ id: 'sheet.mutation.remove-note', params: redoParams }],
                      undoMutations: [{ id: 'sheet.mutation.update-note', params: undoParams }],
                    })
                  }
                } catch { /* */ }
              }
            } else if (existingNotes?.length > 0) {
              // Fallback: no commandService, use facade
              for (const n of existingNotes) {
                try { sheet.getRange(n.row, n.col).deleteNote() } catch { /* */ }
              }
            }
          } catch { /* */ }

          // Re-insert notes from Y.Doc with undo/redo support
          if (s.notes && s.notes.size > 0) {
            for (const [, noteObj] of s.notes.entries()) {
              const noteData = noteObj as { id?: string; row: number; col: number; note: string; width?: number; height?: number }
              const notePayload = {
                id: noteData.id || `note_${noteData.row}_${noteData.col}`,
                row: noteData.row,
                col: noteData.col,
                note: noteData.note,
                width: noteData.width || 160,
                height: noteData.height || 100,
              }
              const redoParams = { unitId, sheetId, row: noteData.row, col: noteData.col, note: notePayload }
              const undoParams = { unitId, sheetId, noteId: notePayload.id, row: noteData.row, col: noteData.col }
              try {
                if (cmdSvc?.syncExecuteCommand('sheet.mutation.update-note', redoParams)) {
                  svc?.pushUndoRedo({
                    unitID: unitId,
                    redoMutations: [{ id: 'sheet.mutation.update-note', params: redoParams }],
                    undoMutations: [{ id: 'sheet.mutation.remove-note', params: undoParams }],
                  })
                } else {
                  sheet.getRange(noteData.row, noteData.col).createOrUpdateNote(notePayload)
                }
              } catch { /* */ }
            }
          }

          // Rebuild conditional formatting from Y.Doc
          if (!isCsvFile(fileName)) {
            // Clear existing CF rules
            try { sheet.clearConditionalFormatRules?.() } catch { /* */ }

            // Re-add from Y.Doc
            const cfRulesJson = s.meta.get('cfRules') as string | undefined
            if (cfRulesJson) {
              try {
                const cfRules = JSON.parse(cfRulesJson) as any[]
                let hasDataBars = false
                // Cap CF ranges — Excel files often have ranges to row 1,048,576
                // (entire column) which freezes Univer's evaluator. Cap to data
                // extent + generous buffer for growth, max 10,000 rows.
                const maxDataRow = Math.min(Math.max(rows * 2, rows + 100), 100000)
                for (const cfRule of cfRules) {
                  const univerRule = {
                    ...cfRule,
                    ranges: (cfRule.ranges || []).map((r: any) => ({
                      startRow: r.startRow + 1,
                      startColumn: r.startColumn,
                      endRow: Math.min(r.endRow + 1, maxDataRow),
                      endColumn: Math.min(r.endColumn, cols - 1),
                    })),
                  }
                  try { sheet.addConditionalFormattingRule?.(univerRule) } catch { /* */ }
                  if (cfRule.rule?.type === 'dataBar') hasDataBars = true
                }
                // Data bar nudge: re-set values in data bar ranges to trigger CF calc
                if (hasDataBars) {
                  for (const cfRule of cfRules) {
                    if (cfRule.rule?.type !== 'dataBar') continue
                    for (const range of cfRule.ranges || []) {
                      const sr = range.startRow + 1, er = range.endRow + 1
                      const sc = range.startColumn, ec = range.endColumn
                      const nudgeRows: any[][] = []
                      for (let r = sr; r <= er; r++) {
                        const row: any[] = []
                        for (let c = sc; c <= ec; c++) {
                          const val = sheet.getRange(r, c).getValue()
                          row.push({ v: val ?? '' })
                        }
                        nudgeRows.push(row)
                      }
                      try {
                        sheet.getRange(sr, sc, er - sr + 1, ec - sc + 1).setValues(nudgeRows)
                      } catch { /* */ }
                    }
                  }
                }
              } catch { /* parse error */ }
            }
          }
        }
      }
    } finally {
      applyingRemoteRef.current = false
    }

    // End undo batch — keep the entry so Ctrl+Z can undo the bulk update
    if (batchDisposeRef.current) {
      batchDisposeRef.current.dispose()
      batchDisposeRef.current = null
    }

    // Get the batch entry we just created — we'll merge drawing undo entries into it
    const batchEntry = svc?.pitchTopUndoElement?.()

    // Rebuild float drawings AFTER the undo batch. Drawing COMMAND APIs handle
    // JSON1 ops correctly on their own, but fail when replayed inside a large
    // batched undo (Univer's executor short-circuits on first mutation failure).
    // Execute them independently, then merge their undo entries into the batch.
    applyingRemoteRef.current = true
    try {
      for (const dr of deferredDrawingRebuilds) {
        // Delete existing Univer drawings
        try {
          const existingImages = dr.sheet.getImages?.()
          if (existingImages?.length > 0) {
            for (const img of existingImages) {
              try { dr.sheet.deleteImages([img]) } catch { /* */ }
            }
          }
        } catch { /* */ }
        // Re-insert from Y.Doc
        const sheetImages: any[] = []
        for (const [, drawingObj] of dr.drawings.entries()) {
          const drawing = drawingObj as Record<string, any>
          if (drawing.type !== 'image' || !drawing.imageUrl) continue
          sheetImages.push(yDocDrawingToSheetImage(drawing, dr.workbookId, dr.sheetId, dr.sheet))
        }
        if (sheetImages.length > 0) {
          // Reverse so later drawings (higher z-order in Excel) render on top in Univer
          sheetImages.reverse()
          try { dr.sheet.insertImages(sheetImages) } catch { /* */ }
        }

        // Render charts in a microtask — renderChartToSvg uses flushSync which
        // cannot be called during a React render cycle (this useEffect)
        const sMaps = getSheetMaps(doc, dr.sheetId)
        if (sMaps?.charts && sMaps.charts.size > 0) {
          const chartsMaps = sMaps.charts
          const cellsMap = sMaps.cells
          const { workbookId: wbId, sheetId: sid, sheet: sh } = dr
          queueMicrotask(() => {
            applyingRemoteRef.current = true
            try {
              const chartImages = renderChartsToSheet(chartsMaps, cellsMap, wbId, sid, sh, buildSheetNameToCellsMap(doc))
              if (chartImages.length > 0) {
                // renderChartsToSheet already returns charts reversed for z-order
                try { sh.insertImages(chartImages) } catch { /* */ }
              }
            } finally {
              applyingRemoteRef.current = false
            }
            // Clear undo entries from chart insertion during bulk update
            const svc = undoRedoServiceRef.current
            if (svc) {
              const wbObj = univerRef.current?.univerAPI?.getActiveWorkbook()
              if (wbObj) svc.clearUndoRedo(wbObj.getId())
            }
          })
        }
      }
    } finally {
      applyingRemoteRef.current = false
    }

    // Merge drawing undo entries into the batch entry.
    // Drawing commands pushed their own undo entries (on top of the batch entry).
    // Pop them off and append their mutations into the batch entry.
    if (batchEntry && svc && deferredDrawingRebuilds.length > 0) {
      const undoStack = (svc as any)._getUndoStackForFocused?.()
      let topEntry = svc.pitchTopUndoElement?.()
      while (topEntry && topEntry !== batchEntry) {
        if (undoStack?.length > 0) {
          const popped = undoStack.pop()
          if (popped) {
            // Prepend drawing mutations so they execute FIRST during undo.
            // Cell mutations in the batch may fail (causing lm to short-circuit),
            // so drawings must run before any potential failure.
            batchEntry.undoMutations.unshift(...popped.undoMutations)
            batchEntry.redoMutations.push(...popped.redoMutations)
          }
        } else {
          break
        }
        topEntry = svc.pitchTopUndoElement?.()
      }
    }

    needsRebaselineRef.current = true

    // Clear the ref now that rebuild is done — observers can resume normal processing
    bulkUpdatingRef.current = false
  }, [bulkUpdating, bulkUpdatingRef, doc])

  useEffect(() => {
    if (onSaveRef) onSaveRef.current = save
    return () => { if (onSaveRef) onSaveRef.current = null }
  }, [save, onSaveRef])

  useEffect(() => {
    if (onDiscardRef) onDiscardRef.current = discard
    return () => { if (onDiscardRef) onDiscardRef.current = null }
  }, [discard, onDiscardRef])

  // Initialize Univer once synced
  useEffect(() => {
    if (!synced) return
    const container = containerRef.current
    if (!container) return

    let disposed = false

    function tryInit() {
      if (container!.offsetWidth === 0 || container!.offsetHeight === 0) {
        const ro = new ResizeObserver((entries) => {
          const entry = entries[0]
          if (entry && entry.contentRect.width > 0 && entry.contentRect.height > 0) {
            ro.disconnect()
            if (!disposed) init()
          }
        })
        ro.observe(container!)
        return () => { ro.disconnect() }
      }
      init()
      return undefined
    }

    let cleanupObserver = tryInit()

    async function init() {
      try {
        // Map app language to Univer locale
        const langMap: Record<string, { localeType: string; imports: Record<string, () => Promise<any>> }> = {
          'zh-CN': {
            localeType: 'zhCN',
            imports: {
              core: () => import('@univerjs/presets/preset-sheets-core/locales/zh-CN'),
              cf: () => import('@univerjs/presets/preset-sheets-conditional-formatting/locales/zh-CN'),
              drawing: () => import('@univerjs/presets/preset-sheets-drawing/locales/zh-CN'),
              hyperLink: () => import('@univerjs/presets/preset-sheets-hyper-link/locales/zh-CN'),
              note: () => import('@univerjs/presets/preset-sheets-note/locales/zh-CN'),
            },
          },
          'zh-TW': {
            localeType: 'zhTW',
            imports: {
              core: () => import('@univerjs/presets/preset-sheets-core/locales/zh-TW'),
              cf: () => import('@univerjs/presets/preset-sheets-conditional-formatting/locales/zh-TW'),
              drawing: () => import('@univerjs/presets/preset-sheets-drawing/locales/zh-TW'),
              hyperLink: () => import('@univerjs/presets/preset-sheets-hyper-link/locales/zh-TW'),
              note: () => import('@univerjs/presets/preset-sheets-note/locales/zh-TW'),
            },
          },
        }
        const appLang = localStorage.getItem('papan-language') || navigator.language || 'en'
        const langConfig = langMap[appLang]

        const li = langConfig?.imports
        const [
          { createUniver, LocaleType },
          { UniverSheetsCorePreset },
          { UniverSheetsConditionalFormattingPreset },
          { UniverSheetsDrawingPreset },
          { UniverSheetsHyperLinkPreset },
          { UniverSheetsNotePreset },
          sheetsCoreLocale, sheetsCfLocale, sheetsDrawingLocale, sheetsHyperLinkLocale, sheetsNoteLocale,
        ] = await Promise.all([
          import('@univerjs/presets'),
          import('@univerjs/preset-sheets-core'),
          import('@univerjs/preset-sheets-conditional-formatting'),
          import('@univerjs/preset-sheets-drawing'),
          import('@univerjs/preset-sheets-hyper-link'),
          import('@univerjs/preset-sheets-note'),
          li ? li.core() : import('@univerjs/presets/preset-sheets-core/locales/en-US'),
          li ? li.cf() : import('@univerjs/presets/preset-sheets-conditional-formatting/locales/en-US'),
          li ? li.drawing() : import('@univerjs/presets/preset-sheets-drawing/locales/en-US'),
          li ? li.hyperLink() : import('@univerjs/presets/preset-sheets-hyper-link/locales/en-US'),
          li ? li.note() : import('@univerjs/presets/preset-sheets-note/locales/en-US'),
        ])

        if (disposed) return

        const univerLocaleType = langConfig ? langConfig.localeType as keyof typeof LocaleType : 'EN_US'
        const resolvedLocale = (LocaleType as any)[univerLocaleType] || LocaleType.EN_US

        // Build all sheets from nested Y.Doc model
        const order = getSheetOrder(doc)
        const sheets: Record<string, any> = {}
        let firstSheetCellData: Record<number, Record<number, any>> = {}

        for (const sheetId of order) {
          const s = getSheetMaps(doc, sheetId)
          if (!s) continue
          void ((s.meta.get('rows') as number) || 0)
          void ((s.meta.get('cols') as number) || 0)
          const { config, cellData } = buildSheetConfig(sheetId, s)
          sheets[sheetId] = config
          if (sheetId === order[0]) firstSheetCellData = cellData
        }

        // Create Univer instance
        const { univer, univerAPI } = createUniver({
          locale: resolvedLocale,
          locales: {
            [resolvedLocale]: deepMergeLocales(
              sheetsCoreLocale.default, sheetsCfLocale.default, sheetsDrawingLocale.default,
              sheetsHyperLinkLocale.default, sheetsNoteLocale.default,
              {
                ribbon: {
                  insertDesc: appLang === 'zh-CN' ? '插入图片、链接和备注。'
                    : appLang === 'zh-TW' ? '插入圖片、連結和備註。'
                    : 'Insert images, links and notes.',
                },
              },
            ),
          },
          presets: [
            UniverSheetsCorePreset({
              container: container as HTMLElement,
              header: !isPreview,
              toolbar: !isPreview,
              formulaBar: !isPreview,
              menu: isPreview ? {} : buildMenuConfig(fileName, isWorkbookGroup),
              contextMenu: !isPreview,
              // Sheet bar always visible for CSV — "+" button creates/links workbook group sheets
            }),
            ...(isCsvFile(fileName) ? [] : [
              UniverSheetsDrawingPreset(),
              UniverSheetsConditionalFormattingPreset(),
              UniverSheetsHyperLinkPreset(),
              UniverSheetsNotePreset(),
            ]),
          ],
        })

        if (disposed) {
          univer.dispose()
          return
        }

        // Build CF resources for workbook initialization (must be passed to createWorkbook
        // so the CF plugin loads rules during init — adding rules after init won't render data bars)
        const resources: Array<{ name: string; data: string }> = []
        if (!isCsvFile(fileName)) {
          const cfResourceData: Record<string, any[]> = {}
          for (const sheetId of order) {
            const s = getSheetMaps(doc, sheetId)
            if (!s) continue
            const cfRulesJson = s.meta.get('cfRules') as string | undefined
            if (!cfRulesJson) continue
            try {
              const cfRules = JSON.parse(cfRulesJson) as Array<{
                ranges: Array<{ startRow: number; startColumn: number; endRow: number; endColumn: number }>
                cfId: string
                stopIfTrue: boolean
                rule: any
              }>
              if (cfRules.length > 0) {
                // Offset ranges: Y.Doc rows are 0-indexed (data), Univer row 0 = header
                cfResourceData[sheetId] = cfRules.map(cfRule => ({
                  ...cfRule,
                  ranges: cfRule.ranges.map(r => ({
                    startRow: r.startRow + 1,
                    startColumn: r.startColumn,
                    endRow: r.endRow + 1,
                    endColumn: r.endColumn,
                  })),
                }))
              }
            } catch (e) {
              console.warn('[UniverModifier] Failed to parse CF rules:', e)
            }
          }
          if (Object.keys(cfResourceData).length > 0) {
            resources.push({
              name: 'SHEET_CONDITIONAL_FORMATTING_PLUGIN',
              data: JSON.stringify(cfResourceData),
            })
          }
        }

        const wb_ = univerAPI.createWorkbook({
          name: fileName || 'Sheet',
          sheets,
          sheetOrder: order,
          resources,
        })
        // #823: the state may have arrived before this workbook existed (the server sends it before the sync)
        if (fileLockedRef.current) { try { wb_?.setEditable(false) } catch { /* best-effort */ } }

        // Activate the sheet matching the opened fileId (instead of defaulting to first sheet)
        if (wb_ && fileId && order.includes(fileId) && order[0] !== fileId) {
          const targetSheet = wb_.getSheetBySheetId(fileId)
          if (targetSheet) wb_.setActiveSheet(targetSheet)
        }

        // In preview mode, set all sheets to read-only via permission system
        if (isPreview && wb_) {
          for (const sid of order) {
            const sheet = wb_.getSheetBySheetId(sid)
            if (sheet) {
              try { sheet.getWorksheetPermission()?.setReadOnly() } catch { /* */ }
            }
          }
        }

        // ── Nudge cells in CF data bar ranges ──────────────────────────
        // createWorkbook() populates cell data directly without firing mutation
        // commands. The CF data bar plugin listens for SetRangeValues commands to
        // calculate bar widths. Re-setting cell values via the command system
        // triggers those commands so data bars render on first load.
        // We use cellData from buildSheetConfig (not getValue() which returns
        // column headers for empty cells in Univer).
        if (resources.length > 0) {
          applyingRemoteRef.current = true
          try {
            const wb = univerAPI.getActiveWorkbook()
            if (wb) {
              for (const res of resources) {
                if (res.name !== 'SHEET_CONDITIONAL_FORMATTING_PLUGIN') continue
                const cfData = JSON.parse(res.data) as Record<string, Array<{ ranges: Array<{ startRow: number; startColumn: number; endRow: number; endColumn: number }>; rule: { type: string } }>>
                for (const [sheetId, rules] of Object.entries(cfData)) {
                  const sheet = wb.getSheetBySheetId(sheetId)
                  const sheetCellData = sheets[sheetId]?.cellData as Record<number, Record<number, any>> | undefined
                  if (!sheet || !sheetCellData) continue
                  for (const cfRule of rules) {
                    if (cfRule.rule.type !== 'dataBar') continue
                    for (const range of cfRule.ranges) {
                      // Build a values matrix from the already-computed cellData
                      const rows: any[][] = []
                      for (let r = range.startRow; r <= range.endRow; r++) {
                        const row: any[] = []
                        for (let c = range.startColumn; c <= range.endColumn; c++) {
                          const cell = sheetCellData[r]?.[c]
                          row.push(cell ? { v: cell.v } : { v: '' })
                        }
                        rows.push(row)
                      }
                      // setValues fires SetRangeValues command → triggers CF data bar calc
                      const numRows = range.endRow - range.startRow + 1
                      const numCols = range.endColumn - range.startColumn + 1
                      sheet.getRange(range.startRow, range.startColumn, numRows, numCols)
                        .setValues(rows)
                    }
                  }
                }
              }
            }
          } catch (e) {
            console.warn('[UniverModifier] Data bar nudge failed:', e)
          } finally {
            applyingRemoteRef.current = false
          }
        }

        // Get undo/redo, command, and drawing services
        let undoRedoService: any = null
        let sheetDrawingService: any = null
        try {
          const { IUndoRedoService, ICommandService } = await import('@univerjs/core')
          const injector = (univer as any).__getInjector()
          injectorRef.current = injector
          undoRedoService = injector.get(IUndoRedoService)
          undoRedoServiceRef.current = undoRedoService
          commandServiceRef.current = injector.get(ICommandService)
          // Listen for selection changes to notify chat panel
          {
            const selDisposable = commandServiceRef.current.onCommandExecuted((info: any) => {
              if (info?.id === 'sheet.operation.set-selections') {
                window.dispatchEvent(new Event('papan:editor-selection-change'))
              }
            })
            selectionCleanupRef.current = () => selDisposable?.dispose()
          }
          // Intercept sheet commands for workbook groups
          if (isCsvFile(fileName) && commandServiceRef.current) {
            const interceptDisposable = commandServiceRef.current.beforeCommandExecuted((info: any) => {
              // Intercept remove-sheet — show custom confirm with unlink/delete options
              if (info?.id === 'sheet.command.remove-sheet-confirm' || info?.id === 'sheet.confirm.remove-sheet') {
                const wb = univerAPI.getActiveWorkbook()
                // Get the target sheet from command params, fall back to active sheet
                const targetId = info?.params?.subUnitId || wb?.getActiveSheet()?.getSheetId()
                const targetSheet = targetId ? wb?.getSheetBySheetId(targetId) : wb?.getActiveSheet()
                const sheetId = targetSheet?.getSheetId() || targetId
                const sheetName = targetSheet?.getSheetName() || 'this sheet'
                const sheetCount = wb?.getSheets()?.length ?? 0
                const isSelf = sheetId === fileId  // unlinking the tab's own CSV
                if (sheetId) {
                  window.dispatchEvent(new CustomEvent('papan:wb-remove-sheet-request', {
                    detail: { sheetId, sheetName, isSelf, sheetCount, sourceFileId: fileId },
                  }))
                }
                throw new Error('Intercepted: use workbook group unlink dialog')
              }
              // Intercept copy-sheet — duplicate the CSV file + link as new sheet
              if (info?.id === 'sheet.command.copy-sheet') {
                const wb = univerAPI.getActiveWorkbook()
                const targetId = info?.params?.subUnitId || wb?.getActiveSheet()?.getSheetId()
                if (targetId) {
                  window.dispatchEvent(new CustomEvent('papan:wb-add-sheet', {
                    detail: { type: 'copy', sourceSheetId: targetId, sourceFileId: fileId },
                  }))
                }
                throw new Error('Intercepted: use workbook group copy-sheet')
              }
              if (info?.id === 'sheet.command.insert-sheet') {
                const sheets = univerAPI.getActiveWorkbook()?.getSheets() ?? []
                const sheetNames = sheets.map((s: any) => s.getSheetName?.() || '').filter(Boolean)
                onWbAddSheetRequest?.(sheets.length, sheetNames)
                throw new Error('Intercepted: use workbook group add-sheet dialog')
              }
            })
            // Listen for sheet rename + reorder → sync to Y.Doc
            const wbCommandDisposable = commandServiceRef.current.onCommandExecuted((info: any) => {
              // Sheet tab rename — write name + update formula refs in Y.Doc
              if (info?.id === 'sheet.mutation.set-worksheet-name' && info?.params) {
                const { subUnitId, name: newName } = info.params as { subUnitId?: string; name?: string }
                if (subUnitId && newName && !applyingRemoteRef.current) {
                  const sheetsMap = doc.getMap('sheets')
                  const sheetMap = sheetsMap.get(subUnitId) as Y.Map<unknown> | undefined
                  const oldName = sheetMap?.get('name') as string | undefined
                  if (sheetMap && oldName && oldName !== newName) {
                    doc.transact(() => {
                      // Update sheet display name
                      sheetMap.set('name', newName)

                      // Update cross-sheet formula references in ALL sheets
                      const escaped = oldName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
                      for (const sid of getSheetOrder(doc)) {
                        const sm = sheetsMap.get(sid) as Y.Map<unknown> | undefined
                        const formulas = sm?.get('formulas') as Y.Map<unknown> | undefined
                        if (!formulas) continue
                        for (const [key, formula] of formulas.entries()) {
                          const f = formula as string
                          if (!f.includes(oldName)) continue
                          const updated = f
                            .replace(new RegExp(`'${escaped}'!`, 'g'), `'${newName}'!`)
                            .replace(new RegExp(`(^|[=+\\-*/,(])${escaped}!`, 'g'), `$1${newName}!`)
                          if (updated !== f) formulas.set(key, updated)
                        }
                      }
                    })
                  }
                }
              }
              // Sheet tab reorder
              if (info?.id === 'sheet.mutation.set-worksheet-order' && info?.params) {
                const wb = univerAPI.getActiveWorkbook()
                if (!wb) return
                const newOrder = wb.getSheets().map((s: any) => s.getSheetId())
                doc.transact(() => {
                  setSheetOrder(doc, newOrder)
                })
              }
            })
            const prevCleanup = selectionCleanupRef.current
            selectionCleanupRef.current = () => {
              prevCleanup?.()
              interceptDisposable?.dispose()
              wbCommandDisposable?.dispose()
            }
          }
          // Clear undo history so loaded state (including data bar nudges) can't be undone
          const wb = univerAPI.getActiveWorkbook()
          if (undoRedoService && wb) {
            undoRedoService.clearUndoRedo(wb.getId())
          }
          // Get sheet drawing service for reading drawing positions
          if (!isCsvFile(fileName)) {
            try {
              const { ISheetDrawingService } = await import('@univerjs/preset-sheets-drawing')
              sheetDrawingService = injector.get(ISheetDrawingService)
              sheetDrawingServiceRef.current = sheetDrawingService
            } catch {
              // Drawing service not available (e.g. CSV mode)
            }

            // Override image popup menu to hide Edit/Crop for chart drawings
            try {
              const { ComponentManager } = await import('@univerjs/ui')
              const { COMPONENT_IMAGE_POPUP_MENU } = await import('@univerjs/preset-sheets-drawing')
              const componentManager = injector.get(ComponentManager)

              // Get the original component before we override
              const OriginalPopupMenu = componentManager.get(COMPONENT_IMAGE_POPUP_MENU)

              // Wrapper that filters out Edit and Crop for chart_ drawings
              const ChartAwarePopupMenu = (props: any) => {
                const menuItems = props?.popup?.extraProps?.menuItems
                if (menuItems) {
                  // Check if any menu item targets a chart drawing
                  const isChart = menuItems.some((item: any) => {
                    const params = item.commandParams
                    // commandParams can be object with drawingId, or array with objects containing drawingId
                    if (params?.drawingId?.startsWith('chart_')) return true
                    if (params?.drawings) return params.drawings.some((d: any) => d.drawingId?.startsWith('chart_'))
                    return false
                  })
                  if (isChart) {
                    // Keep only Delete (remove Edit and Crop)
                    const filtered = menuItems.filter((item: any) =>
                      item.label !== 'image-popup.edit' && item.label !== 'image-popup.crop'
                    )
                    const filteredProps = {
                      ...props,
                      popup: { ...props.popup, extraProps: { ...props.popup.extraProps, menuItems: filtered } }
                    }
                    return OriginalPopupMenu ? (OriginalPopupMenu as any)(filteredProps) : null
                  }
                }
                return OriginalPopupMenu ? (OriginalPopupMenu as any)(props) : null
              }

              // Skip if already overridden (prevents "already exists" warning on remount)
              if (!(OriginalPopupMenu as any)?._chartAware) {
                (ChartAwarePopupMenu as any)._chartAware = true
                componentManager.register(COMPONENT_IMAGE_POPUP_MENU, ChartAwarePopupMenu)
              }
            } catch {
              // Drawing UI not available
            }

            // Add "Add Note" button to the Insert ribbon (note preset only registers context menu)
            try {
              const { IMenuManagerService, getMenuHiddenObservable, MenuItemType } = await import('@univerjs/ui')
              const { UniverInstanceType } = await import('@univerjs/core')
              const menuManager = injector.get(IMenuManagerService)
              menuManager.mergeMenu({
                ['ribbon.insert.media']: {
                  ['sheet.operation.add-note-popup']: {
                    order: 2,
                    menuItemFactory: (accessor: any) => ({
                      id: 'sheet.operation.add-note-popup',
                      type: MenuItemType.BUTTON,
                      tooltip: 'rightClick.addNote',
                      icon: 'AddNoteIcon',
                      commandId: 'sheet.operation.add-note-popup',
                      hidden$: getMenuHiddenObservable(accessor, UniverInstanceType.UNIVER_SHEET),
                    }),
                  },
                },
              })
            } catch (e) { console.warn('[UniverModifier] Failed to add Note ribbon button:', e) }
          }
        } catch { /* services not available */ }

        univerRef.current = { univer, univerAPI }
        setUniverApi(univerAPI)
        setLoading(false)

        // Skip forceCalculation — Univer's formula engine has a dependency-chain
        // bug where batch recalculation evaluates formulas in the wrong order,
        // producing incorrect results for cells that reference other formula cells.
        // Server-side HyperFormula already computed correct values (stored in Y.Doc
        // cells map), and we keep them as .v in createWorkbook data. Univer will
        // recalculate individual formulas correctly when the user edits them.
        needsRebaselineRef.current = true

        // Undo/redo is registered by its OWN effect below, not from here.
        // This one runs once per document load and captured
        // `registerUndoRedo` as it was AT THAT MOMENT — and the host only
        // hands it over while the tab is the one on screen. Univer takes
        // several seconds to come up, so switching to the drive and back
        // during that load left the spreadsheet with no registration for the
        // rest of its life: Ctrl+Z in the grid walked past it into the
        // drive's file history and restored a file deleted minutes earlier.

        // ── Load Y.Doc drawings into Univer + set up drawing event sync ──
        if (!isCsvFile(fileName)) {
          const wb = univerAPI.getActiveWorkbook()
          const workbookId = wb?.getId()
          if (wb && workbookId) {
            // Helper: insert images via command, or fall back to direct mutation
            // when the command isn't registered (happens with multiple Univer instances
            // due to drawing-ui plugin DI failure in the second instance)
            const insertImagesWithFallback = async (sheet: any, images: any[], sheetId: string) => {
              const cmdSvc = commandServiceRef.current as any
              if (!cmdSvc) return

              // Try the normal command first (works when drawing-ui plugin initialized)
              if (cmdSvc.hasCommand('sheet.command.insert-sheet-image')) {
                for (let attempt = 0; attempt < 10; attempt++) {
                  try {
                    sheet.insertImages(images)
                    return
                  } catch (e: any) {
                    if (e?.message?.includes('not registered') && attempt < 9) {
                      await new Promise(r => setTimeout(r, 200))
                    } else {
                      console.error(`[Drawing Load] insertImages command failed:`, e)
                      break
                    }
                  }
                }
              }

              // Fallback: use ISheetDrawingService + SetDrawingApplyMutation directly
              // This bypasses the command layer that requires drawing-ui plugin
              try {
                const { ISheetDrawingService, SetDrawingApplyMutation, DrawingApplyType } = await import('@univerjs/preset-sheets-drawing')
                const drawingSvc = injectorRef.current!.get(ISheetDrawingService)
                const prepared = images.map((img: any) => ({
                  ...img,
                  unitId: workbookId,
                  subUnitId: sheetId,
                }))
                const batchOp = drawingSvc.getBatchAddOp(prepared)
                cmdSvc.syncExecuteCommand(SetDrawingApplyMutation.id, {
                  op: batchOp.redo,
                  unitId: batchOp.unitId,
                  subUnitId: batchOp.subUnitId,
                  objects: batchOp.objects,
                  type: DrawingApplyType.INSERT,
                })
              } catch (e) {
                console.error(`[Drawing Load] Mutation fallback failed:`, e)
              }
            }

            // Load existing drawings from Y.Doc into Univer for all sheets
            // Load drawings async — API URL images need to be fetched and converted to base64
            ;(async () => {
              applyingRemoteRef.current = true
              try {
                for (const sheetId of order) {
                  const sMaps = getSheetMaps(doc, sheetId)
                  if (!sMaps?.drawings) continue
                  const sheet = wb.getSheetBySheetId(sheetId)
                  if (!sheet) continue

                  const sheetImages: any[] = []
                  for (const [drawingId, drawingObj] of sMaps.drawings.entries()) {
                    const drawing = drawingObj as Record<string, any>
                    if (drawing.type !== 'image' || !drawing.imageUrl) continue

                    // If imageUrl is an API URL, fetch and convert to base64 for Univer
                    let resolvedDrawing = drawing
                    const isApiUrl = typeof drawing.imageUrl === 'string' && !drawing.imageUrl.startsWith('data:')
                    if (isApiUrl) {
                      try {
                        const resp = await sessionFetch(drawing.imageUrl)
                        if (resp.ok) {
                          const blob = await resp.blob()
                          const base64 = await new Promise<string>((resolve) => {
                            const reader = new FileReader()
                            reader.onloadend = () => resolve(reader.result as string)
                            reader.readAsDataURL(blob)
                          })
                          resolvedDrawing = { ...drawing, imageUrl: base64 }
                        }
                      } catch (e) {
                        console.error(`[Drawing Load] ${drawingId}: fetch failed`, e)
                      }
                    }
                    sheetImages.push(yDocDrawingToSheetImage(resolvedDrawing, workbookId, sheetId, sheet))
                  }
                  if (sheetImages.length > 0) {
                    sheetImages.reverse()
                    await insertImagesWithFallback(sheet, sheetImages, sheetId)
                  }
                }

                // Load charts as SVG images
                for (const sheetId of order) {
                  const sMaps = getSheetMaps(doc, sheetId)
                  if (!sMaps?.charts || sMaps.charts.size === 0) continue
                  const sheet = wb.getSheetBySheetId(sheetId)
                  if (!sheet) continue

                  const chartImages = renderChartsToSheet(sMaps.charts, sMaps.cells, workbookId, sheetId, sheet, buildSheetNameToCellsMap(doc))
                  if (chartImages.length > 0) {
                    await insertImagesWithFallback(sheet, chartImages, sheetId)
                  }
                }
              } finally {
                applyingRemoteRef.current = false
              }
              // Clear undo history after chart insertion (async, runs after clearUndoRedo)
              if (undoRedoService) {
                undoRedoService.clearUndoRedo(workbookId)
              }
            })()

            // Load cell images from Y.Doc into Univer
            applyingRemoteRef.current = true
            try {
              const cmdSvc = commandServiceRef.current
              if (cmdSvc) {
                const wb = univerRef.current?.univerAPI?.getActiveWorkbook()
                const workbookId = wb?.getId() || ''
                for (const sheetId of order) {
                  const sMaps = getSheetMaps(doc, sheetId)
                  if (!sMaps?.cellImages || sMaps.cellImages.size === 0) continue
                  for (const [, entry] of sMaps.cellImages.entries()) {
                    const ci = entry as { row: number; col: number; p: any }
                    if (!ci?.p) continue
                    try {
                      cmdSvc.syncExecuteCommand('sheet.mutation.set-range-values', {
                        unitId: workbookId,
                        subUnitId: sheetId,
                        cellValue: { [ci.row]: { [ci.col]: { p: ci.p, t: 1, s: { vt: 1 } } } },
                      })
                    } catch { /* */ }
                  }
                }
              }
            } finally {
              applyingRemoteRef.current = false
            }

            // Clear undo history again (drawing insertion creates undo entries)
            if (undoRedoService) {
              undoRedoService.clearUndoRedo(workbookId)
            }

            // Univer → Y.Doc: listen for drawing events
            const drawingDisposables: Array<{ dispose: () => void }> = []

            // Image changed (move/resize)
            drawingDisposables.push(univerAPI.addEvent(
              univerAPI.Event.OverGridImageChanged,
              (params: any) => {
                if (applyingRemoteRef.current || !readyRef.current) return
                const svc = sheetDrawingServiceRef.current
                if (!svc) return

                doc.transact(() => {
                  for (const fImage of (params.images || [])) {
                    const drawingId = fImage.getId()

                    // Chart images: sync move/resize back to charts Y.Map anchor
                    if (drawingId.startsWith('chart_')) {
                      const chartId = drawingId.replace('chart_', '')
                      let drawingData: any = null
                      for (const sid of getSheetOrder(doc)) {
                        drawingData = svc.getDrawingByParam({ unitId: workbookId, subUnitId: sid, drawingId })
                        if (drawingData) break
                      }
                      if (!drawingData?.sheetTransform) continue
                      const st = drawingData.sheetTransform
                      const targetSheetId = drawingData.subUnitId
                      const sMaps = getSheetMaps(doc, targetSheetId)
                      if (!sMaps?.charts) continue
                      const existing = sMaps.charts.get(chartId) as Record<string, any> | undefined
                      if (!existing) continue

                      // Compute new anchor
                      const newAnchor = {
                        from: {
                          col: st.from.column,
                          row: st.from.row,
                          colOff: Math.round((st.from.columnOffset || 0) * EMU_PER_PIXEL),
                          rowOff: Math.round((st.from.rowOffset || 0) * EMU_PER_PIXEL),
                        },
                        to: {
                          col: st.to.column,
                          row: st.to.row,
                          colOff: Math.round((st.to.columnOffset || 0) * EMU_PER_PIXEL),
                          rowOff: Math.round((st.to.rowOffset || 0) * EMU_PER_PIXEL),
                        },
                      }

                      // Skip if anchor unchanged (avoids cascade during bulk update/discard)
                      const old = existing.anchor
                      if (old &&
                        old.from.col === newAnchor.from.col && old.from.row === newAnchor.from.row &&
                        old.from.colOff === newAnchor.from.colOff && old.from.rowOff === newAnchor.from.rowOff &&
                        old.to.col === newAnchor.to.col && old.to.row === newAnchor.to.row &&
                        old.to.colOff === newAnchor.to.colOff && old.to.rowOff === newAnchor.to.rowOff
                      ) continue

                      sMaps.charts.set(chartId, { ...existing, anchor: newAnchor })
                      dirtySheetIdsRef.current.add(targetSheetId)
                      continue
                    }

                    // Regular images: persist to drawings Y.Map
                    let drawingData: any = null
                    for (const sid of getSheetOrder(doc)) {
                      drawingData = svc.getDrawingByParam({
                        unitId: workbookId,
                        subUnitId: sid,
                        drawingId,
                      })
                      if (drawingData) break
                    }
                    if (!drawingData) continue

                    const targetSheetId = drawingData.subUnitId
                    const sMaps = getSheetMaps(doc, targetSheetId)
                    if (!sMaps?.drawings) continue

                    const existing = sMaps.drawings.get(drawingId) as Record<string, any> | undefined
                    const updated = univerDrawingToYDoc(drawingData, drawingId, existing)
                    sMaps.drawings.set(drawingId, updated)
                    dirtySheetIdsRef.current.add(targetSheetId)
                  }
                }, 'local')
              },
            ))

            // Image removed
            drawingDisposables.push(univerAPI.addEvent(
              univerAPI.Event.OverGridImageRemoved,
              (params: any) => {
                if (applyingRemoteRef.current || !readyRef.current) return

                doc.transact(() => {
                  for (const search of (params.removeImageParams || [])) {
                    // Chart images: delete from charts Y.Map
                    if (search.drawingId?.startsWith('chart_')) {
                      const chartId = search.drawingId.replace('chart_', '')
                      const sMaps = getSheetMaps(doc, search.subUnitId)
                      if (sMaps?.charts?.has(chartId)) {
                        sMaps.charts.delete(chartId)
                        dirtySheetIdsRef.current.add(search.subUnitId)
                      }
                      continue
                    }

                    // Regular images
                    const sMaps = getSheetMaps(doc, search.subUnitId)
                    if (!sMaps?.drawings) continue
                    if (sMaps.drawings.has(search.drawingId)) {
                      sMaps.drawings.delete(search.drawingId)
                      dirtySheetIdsRef.current.add(search.subUnitId)
                    }
                  }
                }, 'local')
              },
            ))

            // Image inserted (user-inserted images via Univer UI)
            drawingDisposables.push(univerAPI.addEvent(
              univerAPI.Event.OverGridImageInserted,
              (params: any) => {
                if (applyingRemoteRef.current || !readyRef.current) return
                const svc = sheetDrawingServiceRef.current
                if (!svc) return

                try {
                doc.transact(() => {
                  for (const fImage of (params.images || [])) {
                    let drawingId: string
                    try {
                      drawingId = fImage.getId()
                    } catch {
                      continue
                    }
                    // Skip chart images — rendered fresh from charts Y.Map
                    if (drawingId.startsWith('chart_')) continue
                    // Try to find which sheet this drawing belongs to
                    let insertedData: any = null
                    const sheetOrder = getSheetOrder(doc)
                    for (const sid of sheetOrder) {
                      try {
                        insertedData = svc.getDrawingByParam({
                          unitId: workbookId,
                          subUnitId: sid,
                          drawingId,
                        })
                      } catch { /* */ }
                      if (insertedData) break
                    }
                    if (!insertedData) continue

                    const targetSheetId = insertedData.subUnitId
                    const sMaps = getSheetMaps(doc, targetSheetId)
                    if (!sMaps?.drawings) continue

                    // For user-inserted images, the source is typically base64.
                    // Store the drawing descriptor with the Univer source for now.
                    // The server will embed it into XLSX on save.
                    const builder = fImage.toBuilder()
                    const source = builder.getSource()

                    // Guard: reject base64 images >5MB decoded (~6.7MB base64 string)
                    const MAX_BASE64_LENGTH = 7 * 1024 * 1024
                    if (typeof source === 'string' && source.length > MAX_BASE64_LENGTH) continue
                    const yDocEntry: Record<string, unknown> = {
                      type: 'image',
                      drawingId,
                      imageUrl: source,
                      imageHash: '',
                      mimeType: '',
                      width: insertedData.transform?.width || 0,
                      height: insertedData.transform?.height || 0,
                    }
                    const updated = univerDrawingToYDoc(insertedData, drawingId, yDocEntry)
                    sMaps.drawings.set(drawingId, updated)
                    dirtySheetIdsRef.current.add(targetSheetId)
                  }
                }, 'local')
                } catch { /* */ }
              },
            ))

            drawingCleanupRef.current = () => {
              for (const d of drawingDisposables) d.dispose()
            }

            // ── Load Y.Doc hyperlinks into Univer ──
            applyingRemoteRef.current = true
            try {
              for (const sheetId of order) {
                const sMaps = getSheetMaps(doc, sheetId)
                if (!sMaps?.hyperlinks || sMaps.hyperlinks.size === 0) continue
                const sheet = wb.getSheetBySheetId(sheetId)
                if (!sheet) continue

                for (const [, linkObj] of sMaps.hyperlinks.entries()) {
                  const link = linkObj as { row: number; col: number; payload: string; display?: string }
                  try {
                    sheet.getRange(link.row, link.col).setHyperLink(link.payload, link.display)
                  } catch { /* */ }
                }
              }
            } finally {
              applyingRemoteRef.current = false
            }

            // ── Load Y.Doc notes into Univer ──
            applyingRemoteRef.current = true
            try {
              for (const sheetId of order) {
                const sMaps = getSheetMaps(doc, sheetId)
                if (!sMaps?.notes || sMaps.notes.size === 0) continue
                const sheet = wb.getSheetBySheetId(sheetId)
                if (!sheet) continue

                for (const [, noteObj] of sMaps.notes.entries()) {
                  const noteData = noteObj as { id?: string; row: number; col: number; note: string; width?: number; height?: number }
                  try {
                    sheet.getRange(noteData.row, noteData.col).createOrUpdateNote({
                      id: noteData.id || `note_${noteData.row}_${noteData.col}`,
                      row: noteData.row,
                      col: noteData.col,
                      note: noteData.note,
                      width: noteData.width || 160,
                      height: noteData.height || 100,
                    })
                  } catch { /* */ }
                }
              }
            } finally {
              applyingRemoteRef.current = false
            }

            // ── Univer → Y.Doc: hyperlink sync via command listener ──
            // HyperLinkModel is registered by class ref (not string ID) so we
            // can't access it via injector.  Listen for executed mutations instead.
            const linkDisposables: Array<{ dispose: () => void }> = []

            if (commandServiceRef.current) {
              const cmdSvc = commandServiceRef.current
              const linkCmdDisposable = cmdSvc.onCommandExecuted((info: any) => {
                if (applyingRemoteRef.current || !readyRef.current) return
                const { id: cmdId, params } = info
                if (!params) return

                if (cmdId === 'sheets.command.add-hyper-link') {
                  // params: { unitId, subUnitId, link: { id, row, column, payload, display } }
                  const { subUnitId: sheetId, link } = params
                  if (!sheetId || !link) return
                  const sMaps = getSheetMaps(doc, sheetId)
                  const hlMap = sMaps?.hyperlinks
                  if (!hlMap) return
                  const yDocRow = link.row  // Direct mapping, no offset
                  if (yDocRow < 0) return
                  const key = `${yDocRow}:${link.column}`
                  // Skip if Y.Doc already has this link (prevents feedback loop
                  // when observer applies remote change via setHyperLink → async command)
                  const existing = hlMap.get(key) as { id?: string; payload?: string } | undefined
                  if (existing?.payload === link.payload) return
                  doc.transact(() => {
                    hlMap.set(key, {
                      id: link.id,
                      row: yDocRow,
                      col: link.column,
                      payload: link.payload,
                      display: link.display || '',
                    })
                  }, 'local')
                } else if (cmdId === 'sheets.command.update-hyper-link') {
                  // params: { unitId, subUnitId, row, column, id, payload: { display, payload } }
                  const { subUnitId: sheetId, id: linkId, payload, row, column } = params
                  if (!sheetId || !linkId || !payload) return
                  const sMaps = getSheetMaps(doc, sheetId)
                  const hlMap = sMaps?.hyperlinks
                  if (!hlMap) return
                  const newPayload = payload.payload
                  const newDisplay = payload.display
                  doc.transact(() => {
                    // Try to find by ID first, fall back to row:col key
                    let found = false
                    for (const [key, val] of hlMap.entries()) {
                      const existing = val as { id?: string; payload?: string; display?: string }
                      if (existing.id === linkId) {
                        // Skip if already up-to-date (prevents feedback loop)
                        if (existing.payload === newPayload && existing.display === newDisplay) { found = true; break }
                        const prev = val as Record<string, any>
                        hlMap.set(key, {
                          ...prev,
                          payload: newPayload ?? prev.payload,
                          display: newDisplay ?? prev.display,
                        })
                        found = true
                        break
                      }
                    }
                    if (!found && row != null && column != null) {
                      const yDocRow = row - 1
                      if (yDocRow < 0) return
                      const key = `${yDocRow}:${column}`
                      const prev = hlMap.get(key) as Record<string, any> | undefined
                      if (prev) {
                        hlMap.set(key, {
                          ...prev,
                          payload: newPayload ?? prev.payload,
                          display: newDisplay ?? prev.display,
                        })
                      }
                    }
                  }, 'local')
                } else if (cmdId === 'sheets.command.cancel-hyper-link') {
                  // params: { unitId, subUnitId, row, column, id }
                  const { subUnitId: sheetId, id: linkId, row, column } = params
                  if (!sheetId || !linkId) return
                  const sMaps = getSheetMaps(doc, sheetId)
                  const hlMap = sMaps?.hyperlinks
                  if (!hlMap) return
                  doc.transact(() => {
                    // Try to find by ID first
                    for (const [key, val] of hlMap.entries()) {
                      const existing = val as { id?: string }
                      if (existing.id === linkId) {
                        hlMap.delete(key)
                        return
                      }
                    }
                    // Fall back to row:col key (direct mapping)
                    if (row != null && column != null) {
                      hlMap.delete(`${row}:${column}`)
                    }
                  }, 'local')
                }
              })
              linkDisposables.push({ dispose: () => linkCmdDisposable.dispose() })
            }

            // ── Univer → Y.Doc: note sync via command listener ──
            // The facade events (SheetNoteAdd/Update/Delete) fire before the mutation
            // and don't carry the note text reliably. Instead, listen for the executed
            // mutations directly via ICommandService.onCommandExecuted.
            const noteDisposables: Array<{ dispose: () => void }> = []

            if (commandServiceRef.current) {
              const cmdSvc = commandServiceRef.current
              const noteCmdDisposable = cmdSvc.onCommandExecuted((info: any) => {
                if (applyingRemoteRef.current || !readyRef.current) return
                const { id: cmdId, params } = info
                if (!params) return

                if (cmdId === 'sheet.mutation.update-note') {
                  // params: { unitId, sheetId, row, col, note: { id, note, width, height, ... }, silent? }
                  const { sheetId, row, col, note, silent } = params
                  if (silent) return
                  if (row == null || col == null || !note) return
                  const sMaps = getSheetMaps(doc, sheetId)
                  if (!sMaps?.notes) return
                  const yDocRow = row - 1
                  if (yDocRow < 0) return
                  doc.transact(() => {
                    sMaps.notes!.set(`${yDocRow}:${col}`, {
                      id: note.id || `note_${Date.now()}`,
                      row: yDocRow,
                      col,
                      note: note.note || '',
                      width: note.width || 160,
                      height: note.height || 100,
                    })
                  }, 'local')
                } else if (cmdId === 'sheet.mutation.remove-note') {
                  // params: { unitId, sheetId, noteId }
                  // No row/col in params — find by noteId in Y.Doc
                  const { sheetId, noteId } = params
                  if (!noteId) return
                  const sMaps = getSheetMaps(doc, sheetId)
                  if (!sMaps?.notes) return
                  doc.transact(() => {
                    for (const [key, val] of sMaps.notes!.entries()) {
                      const noteData = val as { id?: string }
                      if (noteData.id === noteId) {
                        sMaps.notes!.delete(key)
                        break
                      }
                    }
                  }, 'local')
                }
              })
              noteDisposables.push({ dispose: () => noteCmdDisposable.dispose() })
            }

            // ── Univer → Y.Doc: conditional formatting sync via command listener ──
            const cfDisposables: Array<{ dispose: () => void }> = []
            if (commandServiceRef.current && !isCsvFile(fileName)) {
              const cmdSvc = commandServiceRef.current
              const cfCommandIds = new Set([
                'sheet.command.add-conditional-rule',
                'sheet.command.set-conditional-rule',
                'sheet.command.delete-conditional-rule',
                'sheet.command.move-conditional-rule',
                'sheet.command.clear-range-conditional-rule',
                'sheet.command.clear-worksheet-conditional-rule',
              ])
              const cfCmdDisposable = cmdSvc.onCommandExecuted((info: any) => {
                if (applyingRemoteRef.current || !readyRef.current) return
                const { id: cmdId } = info
                if (!cfCommandIds.has(cmdId)) return

                // Read the full CF rule set from Univer and write to Y.Doc
                const activeWb = univerRef.current?.univerAPI?.getActiveWorkbook()
                if (!activeWb) return
                const activeSheet = activeWb.getActiveSheet()
                if (!activeSheet) return
                const activeSheetId = activeSheet.getSheetId()

                let cfRules: any[] = []
                try {
                  cfRules = activeSheet.getConditionalFormattingRules?.() || []
                } catch { /* */ }

                // Convert to Y.Doc format (offset rows back: Univer row - 1 = Y.Doc row)
                const yDocCfRules = cfRules.map((rule: any) => ({
                  ...rule,
                  ranges: (rule.ranges || []).map((r: any) => ({
                    startRow: r.startRow - 1,
                    startColumn: r.startColumn,
                    endRow: r.endRow - 1,
                    endColumn: r.endColumn,
                  })),
                }))

                const sMaps = getSheetMaps(doc, activeSheetId)
                if (!sMaps) return

                const newJson = JSON.stringify(yDocCfRules)
                const existing = sMaps.meta.get('cfRules') as string | undefined
                if (existing === newJson) return  // No change

                doc.transact(() => {
                  sMaps.meta.set('cfRules', newJson)
                }, 'local')
              })
              cfDisposables.push({ dispose: () => cfCmdDisposable.dispose() })
            }

            // ── Chart reference tracking ───────────────────────────
            // When rows/cols are inserted/deleted, update chart cell references
            // so chart data stays bound to the correct cells.
            const chartDisposables: Array<{ dispose: () => void }> = []
            if (commandServiceRef.current && !isCsvFile(fileName)) {
              const cmdSvc = commandServiceRef.current
              const structuralMutations: Record<string, import('./chart-ref-types').StructuralOp> = {
                'sheet.mutation.insert-row': 'insertRow',
                'sheet.mutation.remove-rows': 'deleteRow',
                'sheet.mutation.insert-col': 'insertCol',
                'sheet.mutation.remove-col': 'deleteCol',
              }
              const chartRefDisposable = cmdSvc.onCommandExecuted((info: any) => {
                if (applyingRemoteRef.current || !readyRef.current) return
                const op = structuralMutations[info.id]
                if (!op) return
                const params = info.params
                if (!params?.range || !params?.subUnitId) return

                const sMaps = getSheetMaps(doc, params.subUnitId)
                if (!sMaps?.charts || sMaps.charts.size === 0) return

                // Dynamically import to avoid loading chart-parser in the main bundle
                import('./chartRefUpdate.js').then(({ updateChartsOnStructuralEdit }) => {
                  updateChartsOnStructuralEdit(sMaps.charts, op, params.range)
                }).catch(() => { /* chart ref update unavailable */ })
              })
              chartDisposables.push({ dispose: () => chartRefDisposable.dispose() })

              // Block rotation for chart images: after any set-sheet-image command,
              // check if a chart drawing was rotated and reset angle to 0
              const rotateBlockDisposable = cmdSvc.onCommandExecuted((info: any) => {
                if (info.id !== 'sheet.command.set-sheet-image') return
                const drawings = info.params?.drawings
                if (!Array.isArray(drawings)) return
                const chartsToReset = drawings.filter((d: any) =>
                  d.drawingId?.startsWith('chart_') && d.transform?.angle !== 0 && d.transform?.angle != null
                )
                if (chartsToReset.length === 0) return
                // Reset rotation by re-executing with angle: 0
                const resetDrawings = chartsToReset.map((d: any) => ({
                  ...d,
                  transform: { ...d.transform, angle: 0 },
                }))
                applyingRemoteRef.current = true
                try {
                  cmdSvc.executeCommand('sheet.command.set-sheet-image', { unitId: chartsToReset[0].unitId, drawings: resetDrawings })
                } finally {
                  applyingRemoteRef.current = false
                }
              })
              chartDisposables.push({ dispose: () => rotateBlockDisposable.dispose() })
            }

            drawingCleanupRef.current = () => {
              for (const d of drawingDisposables) d.dispose()
              for (const d of linkDisposables) d.dispose()
              for (const d of noteDisposables) d.dispose()
              for (const d of cfDisposables) d.dispose()
              for (const d of chartDisposables) d.dispose()
            }
          }
        }

        // Populate caches and attach Y.Doc observers to first sheet
        if (order.length > 0) {
          const firstSheet = getSheetMaps(doc, order[0])
          if (firstSheet) {
            populateCachesFromSheet(
              firstSheet, firstSheetCellData,
              cellCacheRef.current, styleCacheRef.current, formulaCacheRef.current,
            )
            observerCleanupRef.current = attachSheetObservers(firstSheet, order[0])
            observedSheetIdRef.current = order[0]
          }
        }

        // Track undo/redo to suppress chart re-render during undo/redo
        let isUndoRedo = false
        univerAPI.onCommandExecuted((cmd: any) => {
          const id = cmd?.id || cmd?.params?.id || ''
          if (id === 'univer.command.undo' || id === 'univer.command.redo') {
            isUndoRedo = true
          }
        })

        // Skip collab sync in preview mode — read-only, no Y.Doc writes
        if (isPreview) {
          readyRef.current = true
          setLoading(false)
          return
        }

        // Listen for Univer commands → debounced sync
        univerAPI.onCommandExecuted(() => {
          if (!readyRef.current || applyingRemoteRef.current) return
          if (syncTimerRef.current) clearTimeout(syncTimerRef.current)
          syncTimerRef.current = setTimeout(() => {
            const wb = univerAPI.getActiveWorkbook()
            if (!wb) return
            const activeSheet = wb.getActiveSheet()
            if (!activeSheet) return
            const activeSheetId = activeSheet.getSheetId()

            // On first command after ready, re-baseline caches from Univer's
            // current state. This catches async formula results, CF plugin
            // side effects, and other normalization that completed after init.
            if (needsRebaselineRef.current) {
              needsRebaselineRef.current = false
              rebaselineCaches(
                activeSheet, activeSheetId,
                cellCacheRef.current, styleCacheRef.current, formulaCacheRef.current,
              )
            }

            // If active sheet changed, rebuild caches
            if (activeSheetId !== observedSheetIdRef.current) {
              switchActiveSheetObservers(activeSheetId)
            }

            const s = getSheetMaps(doc, activeSheetId)
            if (!s) return

            syncUniverToYMap(
              activeSheet, activeSheetId, doc, s,
              cellCacheRef.current, styleCacheRef.current, formulaCacheRef.current,
              dirtySheetIdsRef.current,
            )

            // Re-render charts after sync — skip if triggered by undo/redo
            // (undo already reverts the chart image as part of its own operation)
            const skipChartRerender = isUndoRedo
            isUndoRedo = false  // reset for next cycle
            if (chartRerenderTimerRef.current) clearTimeout(chartRerenderTimerRef.current)
            chartRerenderTimerRef.current = null
            if (skipChartRerender) {
              // undo/redo already reverts chart images — skip re-render
            } else {
            queueMicrotask(() => {
              if (s.charts && s.charts.size > 0) {
                rerenderChartsIfChanged(univerAPI, doc, activeSheetId, chartDataHashCacheRef.current, applyingRemoteRef, undoRedoServiceRef.current)
              }
              const thisSheetName = s.meta.get('name') as string | undefined
              if (thisSheetName) {
                for (const otherSheetId of getSheetOrder(doc)) {
                  if (otherSheetId === activeSheetId) continue
                  const otherMaps = getSheetMaps(doc, otherSheetId)
                  if (!otherMaps?.charts || otherMaps.charts.size === 0) continue
                  let refsThisSheet = false
                  otherMaps.charts.forEach((v: unknown) => {
                    const chart = v as any
                    if (chart?.type !== 'chart') return
                    for (const ser of (chart.series || [])) {
                      for (const ref of [ser.catRef, ser.valRef, ser.bubbleSizeRef]) {
                        if (ref && ref.includes(thisSheetName)) { refsThisSheet = true }
                      }
                    }
                  })
                  if (refsThisSheet) {
                    rerenderChartsIfChanged(univerAPI, doc, otherSheetId, chartDataHashCacheRef.current, applyingRemoteRef, undoRedoServiceRef.current)
                  }
                }
              }
            })
            } // end if (!skipChartRerender)
          }, 150)
        })

        setTimeout(() => {
          if (!disposed) {
            needsRebaselineRef.current = true  // Ensure rebaseline on first command
            readyRef.current = true
          }
        }, 1000)
      } catch (err) {
        // #1316: a dropped answer (its session ended) is no init error — the editor is remounted with the session
        // (an identity switch re-keys the workspace tab; the share view keys its editors by session, `key={sessionId}`)
        const text = errorText(err)
        if (!disposed && text !== null) {
          console.error('[UniverModifier] Init failed:', err)
          setError(text)
          setLoading(false)
        }
      }
    }

    /** Switch observers to a new active sheet and rebuild caches. */
    function switchActiveSheetObservers(newSheetId: string) {
      // Write active sheet to viewSync
      if (viewSync && viewSyncId) {
        if (!viewSync[viewSyncId]) viewSync[viewSyncId] = {}
        viewSync[viewSyncId].activeSheet = newSheetId
      }

      // Detach old observers
      observerCleanupRef.current?.()
      observerCleanupRef.current = null
      observedSheetIdRef.current = null

      const s = getSheetMaps(doc, newSheetId)
      if (!s) return

      // Rebuild caches from the new sheet's Y.Maps
      const cellCache = cellCacheRef.current
      const styleCache = styleCacheRef.current
      const formulaCache = formulaCacheRef.current
      cellCache.clear()
      styleCache.clear()
      formulaCache.clear()

      // Rebuild cell cache from current Y.Map state (direct mapping, no header offset)
      const rows = (s.meta.get('rows') as number) || 0
      const cols = (s.meta.get('cols') as number) || 0

      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const val = s.cells.get(`${r}:${c}`) as string | undefined
          if (val && val !== '') cellCache.set(`${r}:${c}`, val)
        }
      }

      for (const [key, style] of s.styles.entries()) {
        styleCache.set(key, stableStringify(style))
      }
      for (const [key, val] of s.formulas.entries()) {
        formulaCache.set(key, val as string)
      }

      // Attach observers to new sheet
      observerCleanupRef.current = attachSheetObservers(s, newSheetId)
      observedSheetIdRef.current = newSheetId
    }

    /** Attach Y.Map observers for a specific sheet's nested maps. Returns cleanup function. */
    function attachSheetObservers(
      s: { cells: Y.Map<unknown>; meta: Y.Map<unknown>; styles: Y.Map<unknown>; merges: Y.Map<unknown>; formulas: Y.Map<unknown>; drawings: Y.Map<unknown> | null; cellImages: Y.Map<unknown> | null; hyperlinks: Y.Map<unknown> | null; notes: Y.Map<unknown> | null; charts: Y.Map<unknown> | null },
      sheetId: string,
    ): () => void {
      const cellsObserver = (event: Y.YMapEvent<unknown>) => {
        if (event.transaction.origin === 'local') return
        if (bulkUpdatingRef.current) return
        const api = univerRef.current?.univerAPI
        if (!api) return
        const sheet = api.getActiveWorkbook()?.getActiveSheet()
        if (!sheet) return
        if (sheet.getSheetId() !== observedSheetIdRef.current) return

        applyingRemoteRef.current = true
        const cache = cellCacheRef.current
        try {
          for (const [key, change] of event.changes.keys) {
            if (s.formulas.has(key)) continue

            const [r, c] = key.split(':').map(Number)
            if (change.action === 'add' || change.action === 'update') {
              const val = (s.cells.get(key) as string) || ''
              const num = val !== '' ? Number(val) : NaN
              try { sheet.getRange(r, c).setValue(val === '' ? '' : (!isNaN(num) ? num : val)) } catch { /* */ }
              if (val === '') { cache.delete(key) } else { cache.set(key, val) }
            } else if (change.action === 'delete') {
              try { sheet.getRange(r, c).setValue('') } catch { /* */ }
              cache.delete(key)
            }
          }
        } finally {
          applyingRemoteRef.current = false
          needsRebaselineRef.current = true
        }

        // Debounced chart re-render on remote cell changes (same sheet + cross-sheet)
        if (chartRerenderTimerRef.current) clearTimeout(chartRerenderTimerRef.current)
        chartRerenderTimerRef.current = setTimeout(() => {
          chartRerenderTimerRef.current = null
          const api2 = univerRef.current?.univerAPI
          if (!api2) return
          // Re-render charts on this sheet
          if (s.charts && s.charts.size > 0) {
            rerenderChartsIfChanged(api2, doc, sheetId, chartDataHashCacheRef.current, applyingRemoteRef, undoRedoServiceRef.current)
          }
          // Re-render charts on OTHER sheets that reference this sheet
          const thisSheetName = s.meta.get('name') as string | undefined
          if (thisSheetName) {
            for (const otherSheetId of getSheetOrder(doc)) {
              if (otherSheetId === sheetId) continue
              const otherMaps = getSheetMaps(doc, otherSheetId)
              if (!otherMaps?.charts || otherMaps.charts.size === 0) continue
              // Check if any chart on otherSheet references thisSheetName
              let refsThisSheet = false
              otherMaps.charts.forEach((v: unknown) => {
                const chart = v as any
                if (chart?.type !== 'chart') return
                for (const ser of (chart.series || [])) {
                  for (const ref of [ser.catRef, ser.valRef]) {
                    if (ref && ref.includes(thisSheetName)) { refsThisSheet = true }
                  }
                }
              })
              if (refsThisSheet) {
                rerenderChartsIfChanged(api2, doc, otherSheetId, chartDataHashCacheRef.current, applyingRemoteRef, undoRedoServiceRef.current)
              }
            }
          }
        }, 500)
      }

      const metaObserver = (event: Y.YMapEvent<unknown>) => {
        if (event.transaction.origin === 'local') return
        if (bulkUpdatingRef.current) return
        const api = univerRef.current?.univerAPI
        if (!api) return
        const sheet = api.getActiveWorkbook()?.getActiveSheet()
        if (!sheet) return

        // Conditional formatting sync (Y.Doc → Univer)
        if (event.changes.keys.has('cfRules') && !isCsvFile(fileName)) {
          applyingRemoteRef.current = true
          try {
            // Clear existing CF rules
            try { sheet.clearConditionalFormatRules?.() } catch { /* */ }

            // Re-add from Y.Doc
            const cfRulesJson = s.meta.get('cfRules') as string | undefined
            if (cfRulesJson) {
              try {
                const cfRules = JSON.parse(cfRulesJson) as any[]
                let hasDataBars = false
                for (const cfRule of cfRules) {
                  // Direct mapping — no header offset
                  const univerRule = { ...cfRule }
                  try { sheet.addConditionalFormattingRule?.(univerRule) } catch { /* */ }
                  if (cfRule.rule?.type === 'dataBar') hasDataBars = true
                }
                // Data bar nudge
                if (hasDataBars) {
                  for (const cfRule of cfRules) {
                    if (cfRule.rule?.type !== 'dataBar') continue
                    for (const range of cfRule.ranges || []) {
                      const sr = range.startRow + 1, er = range.endRow + 1
                      const sc = range.startColumn, ec = range.endColumn
                      const nudgeRows: any[][] = []
                      for (let r = sr; r <= er; r++) {
                        const row: any[] = []
                        for (let c = sc; c <= ec; c++) {
                          const val = sheet.getRange(r, c).getValue()
                          row.push({ v: val ?? '' })
                        }
                        nudgeRows.push(row)
                      }
                      try {
                        sheet.getRange(sr, sc, er - sr + 1, ec - sc + 1).setValues(nudgeRows)
                      } catch { /* */ }
                    }
                  }
                }
              } catch { /* parse error */ }
            }
          } finally {
            applyingRemoteRef.current = false
          }
        }
      }

      const stylesObserver = (event: Y.YMapEvent<unknown>) => {
        if (event.transaction.origin === 'local') return
        if (bulkUpdatingRef.current) return
        const api = univerRef.current?.univerAPI
        if (!api) return
        const sheet = api.getActiveWorkbook()?.getActiveSheet()
        if (!sheet) return
        if (sheet.getSheetId() !== observedSheetIdRef.current) return

        applyingRemoteRef.current = true
        const sCache = styleCacheRef.current
        try {
          for (const [key, change] of event.changes.keys) {
            if (key.startsWith('__col:')) {
              const colIdx = Number(key.slice(6))
              if (change.action !== 'delete') {
                const colData = s.styles.get(key) as Record<string, any>
                if (colData?.w != null && colData.w > 0) try { sheet.setColumnWidth(colIdx, colData.w) } catch { /* */ }
              }
              sCache.set(key, stableStringify(s.styles.get(key)))
              continue
            }
            if (key.startsWith('__row:')) {
              const rowIdx = Number(key.slice(6))
              if (change.action !== 'delete') {
                const rowDt = s.styles.get(key) as Record<string, any>
                if (rowDt?.h != null && rowDt.h > 0) try { sheet.setRowHeight(rowIdx, rowDt.h) } catch { /* */ }
              }
              sCache.set(key, stableStringify(s.styles.get(key)))
              continue
            }
            if (key.startsWith('__')) continue
            if (key.startsWith('h:')) continue  // Legacy header styles — skip

            const [r, c] = key.split(':').map(Number)
            const univerRow = r; const col = c  // Direct mapping, no offset

            if (change.action === 'delete') {
              applyFullCellStyle(sheet, univerRow, col, {})
              sCache.delete(key)
            } else {
              const style = s.styles.get(key) as Record<string, unknown>
              if (style) {
                applyFullCellStyle(sheet, univerRow, col, style)
                sCache.set(key, stableStringify(style))
              }
            }
          }
        } finally {
          applyingRemoteRef.current = false
          needsRebaselineRef.current = true
        }
      }

      const mergesObserver = (event: Y.YMapEvent<unknown>) => {
        if (event.transaction.origin === 'local') return
        if (bulkUpdatingRef.current) return
        const api = univerRef.current?.univerAPI
        if (!api) return
        const sheet = api.getActiveWorkbook()?.getActiveSheet()
        if (!sheet) return

        applyingRemoteRef.current = true
        try {
          try {
            const existingMerges = sheet.getMergeData() as any[]
            if (existingMerges) {
              for (const fRange of existingMerges) {
                try { fRange.breakApart() } catch { /* */ }
              }
            }
          } catch { /* */ }

          for (const [, mergeStr] of s.merges.entries()) {
            const [start, end] = (mergeStr as string).split('-')
            const [startR, startC] = start.split(':').map(Number)
            const [endR, endC] = end.split(':').map(Number)
            try {
              sheet.getRange(startR + 1, startC, endR - startR + 1, endC - startC + 1).merge()
            } catch { /* */ }
          }
        } finally {
          applyingRemoteRef.current = false
          needsRebaselineRef.current = true
        }
      }

      const formulasObserver = (event: Y.YMapEvent<unknown>) => {
        if (event.transaction.origin === 'local') return
        if (bulkUpdatingRef.current) return
        const api = univerRef.current?.univerAPI
        if (!api) return
        const sheet = api.getActiveWorkbook()?.getActiveSheet()
        if (!sheet) return
        if (sheet.getSheetId() !== observedSheetIdRef.current) return
        // Only update the formula cache — do NOT call setValue on Univer.
        // setValue triggers the formula engine → onCommandExecuted → syncUniverToYMap → loop.
        // The formulas are already in the Y.Doc and will be applied on the next full rebuild.
        const fCache = formulaCacheRef.current
        for (const [key, change] of event.changes.keys) {
          if (change.action === 'add' || change.action === 'update') {
            const formula = s.formulas.get(key) as string
            if (formula) fCache.set(key, formula)
          } else if (change.action === 'delete') {
            fCache.delete(key)
          }
        }
        needsRebaselineRef.current = true
      }

      // Drawings observer (Y.Doc → Univer): remote drawing add/update/remove
      const drawingsObserver = s.drawings ? (event: Y.YMapEvent<unknown>) => {
        if (event.transaction.origin === 'local') return
        if (bulkUpdatingRef.current) return
        const api = univerRef.current?.univerAPI
        if (!api) return
        const wb = api.getActiveWorkbook()
        if (!wb) return
        const sheet = wb.getSheetBySheetId(sheetId)
        if (!sheet) return
        const workbookId = wb.getId()

        applyingRemoteRef.current = true
        try {
          for (const [drawingId, change] of event.changes.keys) {
            if (change.action === 'add' || change.action === 'update') {
              const drawing = s.drawings!.get(drawingId) as Record<string, any>
              if (!drawing || drawing.type !== 'image') continue

              const sheetImage = yDocDrawingToSheetImage(drawing, workbookId, sheetId)

              if (change.action === 'add') {
                try { sheet.insertImages([sheetImage]) } catch { /* */ }
              } else {
                try { sheet.updateImages([sheetImage]) } catch {
                  try {
                    const existing = sheet.getImageById(drawingId)
                    if (existing) sheet.deleteImages([existing])
                    sheet.insertImages([sheetImage])
                  } catch { /* */ }
                }
              }
            } else if (change.action === 'delete') {
              try {
                const existing = sheet.getImageById(drawingId)
                if (existing) sheet.deleteImages([existing])
              } catch { /* */ }
            }
          }
        } finally {
          applyingRemoteRef.current = false
        }
      } : null

      // Cell images observer (Y.Doc → Univer): remote cell image add/update/remove
      const cellImagesObserver = s.cellImages ? (event: Y.YMapEvent<unknown>) => {
        if (event.transaction.origin === 'local') return
        if (bulkUpdatingRef.current) return
        const cmdSvc = commandServiceRef.current
        if (!cmdSvc) return
        const api = univerRef.current?.univerAPI
        if (!api) return
        const wb = api.getActiveWorkbook()
        if (!wb) return
        const unitId = wb.getId()

        applyingRemoteRef.current = true
        try {
          for (const [key, change] of event.changes.keys) {
            const [r, c] = key.split(':').map(Number)
            if (change.action === 'add' || change.action === 'update') {
              const entry = s.cellImages!.get(key) as { row: number; col: number; p: any } | undefined
              if (entry?.p) {
                cmdSvc.syncExecuteCommand('sheet.mutation.set-range-values', {
                  unitId,
                  subUnitId: sheetId,
                  cellValue: { [r]: { [c]: { p: entry.p, t: 1, s: { vt: 1 } } } },
                })
              }
            } else if (change.action === 'delete') {
              cmdSvc.syncExecuteCommand('sheet.mutation.set-range-values', {
                unitId,
                subUnitId: sheetId,
                cellValue: { [r]: { [c]: { p: null } } },
              })
            }
          }
        } finally {
          applyingRemoteRef.current = false
        }
      } : null

      // Hyperlinks observer (Y.Doc → Univer)
      const hyperlinksObserver = s.hyperlinks ? (event: Y.YMapEvent<unknown>) => {
        if (event.transaction.origin === 'local') return
        if (bulkUpdatingRef.current) return
        const api = univerRef.current?.univerAPI
        if (!api) return
        const sheet = api.getActiveWorkbook()?.getSheetBySheetId(sheetId)
        if (!sheet) return

        applyingRemoteRef.current = true
        try {
          for (const [key, change] of event.changes.keys) {
            const [r, c] = key.split(':').map(Number)
            const univerRow = r
            if (change.action === 'add' || change.action === 'update') {
              const link = s.hyperlinks!.get(key) as { payload: string; display?: string }
              if (link?.payload) {
                try {
                  const range = sheet.getRange(univerRow, c)
                  // Cancel existing hyperlinks first
                  const existing = range.getHyperLinks?.()
                  if (existing) for (const hl of existing) try { range.cancelHyperLink(hl.id) } catch { /* */ }
                  range.setHyperLink(link.payload, link.display)
                } catch { /* */ }
              }
            } else if (change.action === 'delete') {
              try {
                const range = sheet.getRange(univerRow, c)
                const existing = range.getHyperLinks?.()
                if (existing) for (const hl of existing) try { range.cancelHyperLink(hl.id) } catch { /* */ }
                // Also clear cell content to remove leftover rich text from p
                range.clearContent()
              } catch { /* */ }
            }
          }
        } finally {
          applyingRemoteRef.current = false
        }
      } : null

      // Notes observer (Y.Doc → Univer)
      const notesObserver = s.notes ? (event: Y.YMapEvent<unknown>) => {
        if (event.transaction.origin === 'local') return
        if (bulkUpdatingRef.current) return
        const api = univerRef.current?.univerAPI
        if (!api) return
        const sheet = api.getActiveWorkbook()?.getSheetBySheetId(sheetId)
        if (!sheet) return

        applyingRemoteRef.current = true
        try {
          for (const [key, change] of event.changes.keys) {
            const [r, c] = key.split(':').map(Number)
            const univerRow = r
            if (change.action === 'add' || change.action === 'update') {
              const noteData = s.notes!.get(key) as { id?: string; note: string; width?: number; height?: number }
              if (noteData) {
                try {
                  sheet.getRange(univerRow, c).createOrUpdateNote({
                    id: noteData.id || `note_${r}_${c}`,
                    row: univerRow,
                    col: c,
                    note: noteData.note ?? '',
                    width: noteData.width || 160,
                    height: noteData.height || 100,
                  })
                } catch { /* */ }
              }
            } else if (change.action === 'delete') {
              try { sheet.getRange(univerRow, c).deleteNote() } catch { /* */ }
            }
          }
        } finally {
          applyingRemoteRef.current = false
        }
      } : null

      // Charts observer (Y.Doc → Univer): remote chart add/update/remove
      const chartsObserver = s.charts ? (event: Y.YMapEvent<unknown>) => {
        if (event.transaction.origin === 'local') return
        if (bulkUpdatingRef.current) return
        if (applyingRemoteRef.current) return
        const api = univerRef.current?.univerAPI
        if (!api) return
        const wb = api.getActiveWorkbook()
        if (!wb) return
        const sheet = wb.getSheetBySheetId(sheetId)
        if (!sheet) return
        const workbookId = wb.getId()

        applyingRemoteRef.current = true
        try {
          for (const [chartId, change] of event.changes.keys) {
            const drawingId = `chart_${chartId}`
            if (change.action === 'add' || change.action === 'update') {
              // Re-render this chart from the updated Y.Doc entry
              try {
                const existing = sheet.getImageById(drawingId)
                if (existing) sheet.deleteImages([existing])
              } catch { /* */ }
              const chartImages = renderChartsToSheet(s.charts!, s.cells, workbookId, sheetId, sheet, buildSheetNameToCellsMap(doc))
              const updated = chartImages.find((img: any) => img.drawingId === drawingId)
              if (updated) {
                try { sheet.insertImages([updated]) } catch { /* */ }
              }
            } else if (change.action === 'delete') {
              try {
                const existing = sheet.getImageById(drawingId)
                if (existing) sheet.deleteImages([existing])
              } catch { /* */ }
            }
          }
        } finally {
          applyingRemoteRef.current = false
        }
      } : null

      s.cells.observe(cellsObserver)
      s.meta.observe(metaObserver)
      s.styles.observe(stylesObserver)
      s.merges.observe(mergesObserver)
      s.formulas.observe(formulasObserver)
      if (drawingsObserver && s.drawings) s.drawings.observe(drawingsObserver)
      if (cellImagesObserver && s.cellImages) s.cellImages.observe(cellImagesObserver)
      if (hyperlinksObserver && s.hyperlinks) s.hyperlinks.observe(hyperlinksObserver)
      if (notesObserver && s.notes) s.notes.observe(notesObserver)
      if (chartsObserver && s.charts) s.charts.observe(chartsObserver)

      return () => {
        s.cells.unobserve(cellsObserver)
        s.meta.unobserve(metaObserver)
        s.styles.unobserve(stylesObserver)
        s.merges.unobserve(mergesObserver)
        s.formulas.unobserve(formulasObserver)
        if (drawingsObserver && s.drawings) s.drawings.unobserve(drawingsObserver)
        if (cellImagesObserver && s.cellImages) s.cellImages.unobserve(cellImagesObserver)
        if (hyperlinksObserver && s.hyperlinks) s.hyperlinks.unobserve(hyperlinksObserver)
        if (notesObserver && s.notes) s.notes.unobserve(notesObserver)
        if (chartsObserver && s.charts) s.charts.unobserve(chartsObserver)
      }
    }

    return () => {
      disposed = true
      readyRef.current = false
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current)
      cellCacheRef.current.clear()
      styleCacheRef.current.clear()
      formulaCacheRef.current.clear()
      observerCleanupRef.current?.()
      observerCleanupRef.current = null
      observedSheetIdRef.current = null
      commandServiceRef.current = null
      sheetDrawingServiceRef.current = null
      drawingCleanupRef.current?.()
      drawingCleanupRef.current = null
      selectionCleanupRef.current?.()
      selectionCleanupRef.current = null
      batchDisposeRef.current?.dispose()
      batchDisposeRef.current = null
      cleanupObserver?.()
      const instance = univerRef.current
      univerRef.current = null
      setUniverApi(null)
      if (instance) {
        queueMicrotask(() => instance.univer.dispose())
      }
    }
  }, [synced, doc, fileName])

  // Restore active sheet from viewSync after Univer is ready
  useEffect(() => {
    if (!synced || !viewSync || !viewSyncId) return
    const stored = viewSync[viewSyncId]?.activeSheet as string | undefined
    if (!stored) return
    // Wait for Univer to be ready, then switch to the stored sheet
    const timer = setTimeout(() => {
      try {
        const api = univerRef.current?.univerAPI
        if (!api) return
        const wb = api.getActiveWorkbook?.()
        if (!wb) return
        const sheet = wb.getSheetBySheetId(stored)
        if (sheet) {
          wb.setActiveSheet(sheet)
        }
      } catch { /* non-critical */ }
    }, 1500) // Univer needs time to init
    return () => clearTimeout(timer)
  }, [synced, viewSync, viewSyncId])

  // Observe workbook map for structural changes (sheet add/remove from remote)
  useEffect(() => {
    if (!synced) return

    const wbMap = doc.getMap('workbook')

    const wbObserver = (event: Y.YMapEvent<unknown>) => {
      if (event.transaction.origin === 'local') return
      const api = univerRef.current?.univerAPI
      if (!api) return
      const wb = api.getActiveWorkbook()
      if (!wb) return

      if (event.changes.keys.has('sheetOrder')) {
        const newOrder = (wbMap.get('sheetOrder') as string[]) || []
        const currentSheets = wb.getSheets().map((s: any) => s.getSheetId())

        // Add new sheets
        for (const sheetId of newOrder) {
          if (!currentSheets.includes(sheetId)) {
            const sMaps = getSheetMaps(doc, sheetId)
            if (!sMaps) continue
            const sheetName = (sMaps.sheet.get('name') as string) || 'Sheet'
            applyingRemoteRef.current = true
            try {
              wb.insertSheet({ id: sheetId, name: sheetName })
            } catch { /* */ }
            applyingRemoteRef.current = false
          }
        }

        // Remove deleted sheets
        for (const existingId of currentSheets) {
          if (!newOrder.includes(existingId)) {
            applyingRemoteRef.current = true
            try {
              const sheet = wb.getSheets().find((s: any) => s.getSheetId() === existingId)
              if (sheet) wb.removeSheet(existingId)
            } catch { /* */ }
            applyingRemoteRef.current = false
          }
        }
      }
    }

    wbMap.observe(wbObserver)

    return () => {
      wbMap.unobserve(wbObserver)
    }
  }, [doc, synced])

  // Ctrl+S save is handled by WorkspaceView (which calls the active tab's save ref).
  // Do NOT register a window keydown handler here — all mounted editors would fire.

  // Expose edit actions to parent
  useImperativeHandle(ref, () => ({
    copy: () => {
      commandServiceRef.current?.executeCommand('univer.command.copy')
    },
    cut: () => {
      commandServiceRef.current?.executeCommand('univer.command.cut')
    },
    paste: () => {
      commandServiceRef.current?.executeCommand('univer.command.paste')
    },
    delete: () => {
      commandServiceRef.current?.executeCommand('sheet.command.clear-selection-content')
    },
    getSelection: () => {
      try {
        const api = univerRef.current?.univerAPI
        if (!api) return null
        const wb = api.getActiveWorkbook()
        if (!wb) return null
        const sheet = wb.getActiveSheet()
        if (!sheet) return null
        const sel = sheet.getSelection()
        if (!sel) return null
        const range = sel.getActiveRange()
        if (!range) return null
        const startRow = range.getRow()
        const startCol = range.getColumn()
        const endRow = range.getLastRow()
        const endCol = range.getLastColumn()
        const width = range.getWidth()
        const height = range.getHeight()
        // Detect row/column selections
        const sheetMaxRows = sheet.getMaxRows?.() ?? MAX_ROWS
        const sheetMaxCols = sheet.getMaxColumns?.() ?? MAX_COLS
        let cellRange: string
        const isFullRows = width >= Math.min(sheetMaxCols, MAX_COLS)
        const isFullCols = height >= Math.min(sheetMaxRows, MAX_ROWS)
        if (isFullRows && !isFullCols) {
          cellRange = startRow === endRow ? `Row ${startRow + 1}` : `Rows ${startRow + 1}–${endRow + 1}`
        } else if (isFullCols && !isFullRows) {
          cellRange = startCol === endCol ? `Col ${colToLetter(startCol)}` : `Cols ${colToLetter(startCol)}–${colToLetter(endCol)}`
        } else {
          cellRange = range.getA1Notation()
        }
        // Read cell values as tab-separated text
        let text = ''
        try {
          const dataRange = sheet.getRange(startRow, startCol, height, width)
          const data = dataRange.getCellDatas()
          if (data) {
            text = (data as any[][]).map(row =>
              row.map(cell => cell?.v != null ? String(cell.v) : '').join('\t')
            ).join('\n')
          }
        } catch { /* non-fatal */ }
        if (!text && width === 1 && height === 1) return null // single empty cell
        const sheetName = sheet.getSheetName?.() || undefined
        return {
          text: text.substring(0, 10000),
          fileName: fileName || '', filePath: '', fileId: fileId || '',
          startLine: 0, endLine: 0,
          kind: 'cells' as const,
          cellRange, sheetName,
        }
      } catch { return null }
    },
    focusEditor: () => { containerRef.current?.focus() },
  }), [fileName, fileId])

  // ─── Sheet Tab Context Menu (CSV workbook groups) ────────────────
  const sheetMenu = useContextMenu<SheetTabMenuState>()
  const appLang = (typeof localStorage !== 'undefined' && localStorage.getItem('papan-language')) || 'en'
  const menuLang = appLang.startsWith('zh-TW') ? 'zh-TW' : appLang.startsWith('zh') ? 'zh-CN' : 'en'
  const labels = getSheetMenuLabel(menuLang)

  // Attach contextmenu handler to the Univer container to intercept sheet tab right-clicks
  useEffect(() => {
    if (!isCsvFile(fileName) || isPreview) return
    const container = containerRef.current
    if (!container) return

    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      const tab = resolveSheetTabFromTarget(target)
      if (!tab) return  // Not a sheet tab click — let Univer handle it

      e.preventDefault()
      e.stopPropagation()
      e.stopImmediatePropagation()

      // Resolve sheetName → sheetId via Univer API
      const wb = univerRef.current?.univerAPI?.getActiveWorkbook()
      const sheets = wb?.getSheets() ?? []
      const sheet = sheets.find((s: any) => s.getSheetName() === tab.sheetName)
      const sheetId = sheet?.getSheetId() || ''

      // Position menu above the clicked tab
      let menuX = e.clientX
      let menuY = e.clientY
      let tabEl: HTMLElement | null = target
      for (let i = 0; i < 8 && tabEl; i++) {
        const cn = typeof tabEl.className === 'string' ? tabEl.className : ''
        if (cn.includes('univer-cursor-pointer') && cn.includes('univer-select-none')) {
          const tabRect = tabEl.getBoundingClientRect()
          menuX = tabRect.left
          menuY = tabRect.top  // will be repositioned above tab after render
          if (menuY < 4) menuY = 4  // don't go off-screen
          break
        }
        tabEl = tabEl.parentElement
      }

      sheetMenu.open({ x: menuX, y: menuY, sheetId, sheetName: tab.sheetName })
    }

    // Suppress right-click on sheet tabs — capture phase + stopImmediatePropagation
    // prevents both browser native menu and Univer's internal menu handler
    container.addEventListener('contextmenu', handler, true)

    // Also intercept mousedown + pointerdown (button=2) to prevent Univer's internal
    // sheet bar menu, which triggers on right-click of non-active tabs during tab switch
    const rightClickBlocker = (e: MouseEvent | PointerEvent) => {
      if (e.button !== 2) return
      const target = e.target as HTMLElement
      const tab = resolveSheetTabFromTarget(target)
      if (tab) {
        e.stopPropagation()
        e.stopImmediatePropagation()
      }
    }
    container.addEventListener('mousedown', rightClickBlocker, true)
    container.addEventListener('pointerdown', rightClickBlocker, true)

    return () => {
      container.removeEventListener('contextmenu', handler, true)
      container.removeEventListener('mousedown', rightClickBlocker, true)
      container.removeEventListener('pointerdown', rightClickBlocker, true)
    }
  }, [fileName, isPreview, sheetMenu.open])

  const handleSheetMenuAction = useCallback((action: string) => {
    const state = sheetMenu.menu
    if (!state) return
    sheetMenu.close()

    const wb = univerRef.current?.univerAPI?.getActiveWorkbook()
    const sheets = wb?.getSheets() ?? []
    const sheetCount = sheets.length
    const isSelf = state.sheetId === fileId

    switch (action) {
      case 'rename': {
        // Activate the target sheet first, then trigger inline rename after Univer settles
        const targetSheet = wb?.getSheetBySheetId(state.sheetId)
        if (targetSheet) wb?.setActiveSheet(targetSheet)
        const sid = state.sheetId
        setTimeout(() => {
          try {
            commandServiceRef.current?.syncExecuteCommand('sheet.operation.rename-sheet', { subUnitId: sid })
          } catch { /* non-critical */ }
        }, 100)
        break
      }
      case 'copy': {
        window.dispatchEvent(new CustomEvent('papan:wb-add-sheet', {
          detail: { type: 'copy', sourceSheetId: state.sheetId, sourceFileId: fileId },
        }))
        break
      }
      case 'relink': {
        window.dispatchEvent(new CustomEvent('papan:wb-relink-sheet', {
          detail: { sheetId: state.sheetId, sheetName: state.sheetName, sourceFileId: fileId },
        }))
        break
      }
      case 'unlink': {
        window.dispatchEvent(new CustomEvent('papan:wb-remove-sheet-request', {
          detail: { sheetId: state.sheetId, sheetName: state.sheetName, isSelf, sheetCount, sourceFileId: fileId },
        }))
        break
      }
      case 'insert': {
        const sheetNames = sheets.map((s: any) => s.getSheetName?.() || '').filter(Boolean)
        onWbAddSheetRequest?.(sheetCount, sheetNames)
        break
      }
    }
  }, [sheetMenu.menu, sheetMenu.close, fileId, onWbAddSheetRequest])

  // Reposition sheet context menu above the tab after it renders (measure actual height)
  useEffect(() => {
    if (!sheetMenu.menu || !sheetMenu.menuRef.current) return
    const el = sheetMenu.menuRef.current
    const rect = el.getBoundingClientRect()
    const above = sheetMenu.menu.y - rect.height - 4
    el.style.top = `${Math.max(4, above)}px`
    el.style.left = `${sheetMenu.menu.x - 4}px`
  }, [sheetMenu.menu])

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center themed-text-muted">
        <p>Failed to load spreadsheet: {error}</p>
      </div>
    )
  }

  if (forbidden) return <ForbiddenOverlay />

  return (
    <div className="flex-1 flex flex-col overflow-hidden relative" style={{ minHeight: 0 }}>
      <FileStateBanner state={isPreview ? null : yjsResult.fileState} />
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center themed-bg-primary z-10">
          <Loader2 className="animate-spin mr-2 themed-text-muted" size={24} />
          <span className="themed-text-muted">
            {synced ? 'Loading spreadsheet...' : 'Connecting...'}
          </span>
        </div>
      )}
      <div ref={containerRef} className={isPreview ? 'preview-mode' : ''} style={{ width: '100%', height: '100%', flex: 1, minHeight: 0 }} />
      {isPreview && (
        <style>{`
          /* Hide add sheet button (plus icon) in preview, keep sheet list menu */
          .preview-mode .univerjs-icon-increase-icon { display: none !important; }
        `}</style>
      )}

      {/* Sheet tab context menu — uses Univer's own CSS classes for visual consistency */}
      {sheetMenu.menu && (
        <div
          ref={sheetMenu.menuRef}
          className="univer-menu univer-menu-root univer-menu-vertical"
          style={{ position: 'fixed', left: sheetMenu.menu.x, top: sheetMenu.menu.y, zIndex: 9999 }}
        >
          <div className="univer-menu-item-group">
            <ul className="univer-menu-item-group-list">
              <li className="univer-menu-item" onClick={() => handleSheetMenuAction('rename')}>
                <span className="univer-inline-flex univer-gap-2 univer-items-center"><span>{labels.rename}</span></span>
              </li>
              {isWorkbookGroup && <>
                <li className="univer-menu-item" onClick={() => handleSheetMenuAction('copy')}>
                  <span className="univer-inline-flex univer-gap-2 univer-items-center"><span>{labels.copy}</span></span>
                </li>
                <li className="univer-menu-item-divider" />
                <li className="univer-menu-item" onClick={() => handleSheetMenuAction('relink')}>
                  <span className="univer-inline-flex univer-gap-2 univer-items-center"><span>{labels.relink}</span></span>
                </li>
                <li className="univer-menu-item" onClick={() => handleSheetMenuAction('unlink')}>
                  <span className="univer-inline-flex univer-gap-2 univer-items-center"><span>{labels.unlink}</span></span>
                </li>
              </>}
              <li className="univer-menu-item-divider" />
              <li className="univer-menu-item" onClick={() => handleSheetMenuAction('insert')}>
                <span className="univer-inline-flex univer-gap-2 univer-items-center"><span>{labels.insert}</span></span>
              </li>
            </ul>
          </div>
        </div>
      )}
    </div>
  )
})

// ─── Re-baseline caches from Univer's normalized state ─────────────
// Reads Univer's current state into the caches without touching Y.Doc.
// Called right before readyRef=true so that the first real sync doesn't
// detect false diffs from Univer normalization (e.g. style defaults,
// number format changes, CF plugin side effects).

function rebaselineCaches(
  sheet: any,
  sheetId: string,
  cellCache: Map<string, string>,
  styleCache: Map<string, string>,
  formulaCache: Map<string, string>,
) {
  const dataRange = sheet.getDataRange()
  if (!dataRange) return

  let stylesLookup: Record<string, Record<string, any>> = {}
  let sheetSnapshot: any = null
  try {
    const wb = sheet.getWorkbook?.()
    const snapshot = wb?.getSnapshot?.()
    if (snapshot?.styles) stylesLookup = snapshot.styles
    if (snapshot?.sheets?.[sheetId]) sheetSnapshot = snapshot.sheets[sheetId]
  } catch { /* */ }

  const cellDatas: (any | null)[][] = dataRange.getCellDatas() || []

  cellCache.clear()
  styleCache.clear()
  formulaCache.clear()

  for (let r = 0; r < cellDatas.length; r++) {
    const row = cellDatas[r]
    if (!row) continue
    for (let c = 0; c < row.length; c++) {
      const cell = row[c]
      if (cell?.f && typeof cell.f === 'string') {
        formulaCache.set(`${r}:${c}`, cell.f as string)
      }
      const val = cell?.v
      if (val != null && val !== '') {
        const strVal = typeof val === 'number'
          ? String(Math.round(val * 1e6) / 1e6)
          : String(val)
        cellCache.set(`${r}:${c}`, strVal)
      }
      const styleKey = `${r}:${c}`
      // Get style from cell.s only (not getBackgrounds) to avoid capturing
      // CF-computed backgrounds as cell styles.
      const styleRef = cell?.s
      let resolvedStyle: Record<string, any> | null = null
      if (typeof styleRef === 'string' && stylesLookup[styleRef]) {
        resolvedStyle = cleanResolvedStyle(stylesLookup[styleRef])
      } else if (typeof styleRef === 'object' && styleRef !== null) {
        resolvedStyle = cleanResolvedStyle(styleRef)
      }
      if (resolvedStyle && Object.keys(resolvedStyle).length > 0) {
        styleCache.set(styleKey, stableStringify(resolvedStyle))
      }
    }
  }

  // Column/row data from snapshot (must match syncUniverToYMap logic)
  if (sheetSnapshot) {
    const defaultColWidth = sheetSnapshot.defaultColumnWidth || 88
    const defaultRowHeight = sheetSnapshot.defaultRowHeight || 24

    if (sheetSnapshot.columnData) {
      for (const [colIdx, colData] of Object.entries(sheetSnapshot.columnData as Record<string, any>)) {
        const entry: Record<string, any> = {}
        if (colData.w != null && colData.w !== defaultColWidth) entry.w = colData.w
        if (colData.hd) entry.hd = colData.hd
        if (typeof colData.s === 'string' && stylesLookup[colData.s]) {
          const cleaned = cleanResolvedStyle(stylesLookup[colData.s])
          if (Object.keys(cleaned).length > 0) entry.s = cleaned
        }
        if (Object.keys(entry).length > 0) {
          styleCache.set(`__col:${colIdx}`, stableStringify(entry))
        }
      }
    }

    if (sheetSnapshot.rowData) {
      for (const [rowIdx, rowDt] of Object.entries(sheetSnapshot.rowData as Record<string, any>)) {
        const entry: Record<string, any> = {}
        if (rowDt.h != null && rowDt.h !== defaultRowHeight) entry.h = rowDt.h
        if (rowDt.hd) entry.hd = rowDt.hd
        if (rowDt.ia != null) entry.ia = rowDt.ia
        if (typeof rowDt.s === 'string' && stylesLookup[rowDt.s]) {
          const cleaned = cleanResolvedStyle(stylesLookup[rowDt.s])
          if (Object.keys(cleaned).length > 0) entry.s = cleaned
        }
        if (Object.keys(entry).length > 0) {
          styleCache.set(`__row:${rowIdx}`, stableStringify(entry))
        }
      }
    }
  }
}

// ─── Sync Univer → Y.Doc (per-sheet) ──────────────────────────────

function syncUniverToYMap(
  sheet: any,
  sheetId: string,
  doc: Y.Doc,
  s: { cells: Y.Map<unknown>; meta: Y.Map<unknown>; styles: Y.Map<unknown>; merges: Y.Map<unknown>; formulas: Y.Map<unknown>; hyperlinks?: Y.Map<unknown> | null; cellImages?: Y.Map<unknown> | null },
  cellCache: Map<string, string>,
  styleCache: Map<string, string>,
  formulaCache: Map<string, string>,
  dirtySheetIds?: Set<string>,
) {
  const dataRange = sheet.getDataRange()
  if (!dataRange) return

  // Get the data range's start offset — getDataRange() returns the smallest
  // bounding box of non-empty cells, which may NOT start at row 0.
  // We need the offset to map array indices back to actual Univer row/col numbers.
  const rangeStartRow = dataRange.getRow?.() ?? 0
  const rangeStartCol = dataRange.getColumn?.() ?? 0

  // Get workbook snapshot for styles, column/row data
  let stylesLookup: Record<string, Record<string, any>> = {}
  let sheetSnapshot: any = null
  try {
    const wb = sheet.getWorkbook?.()
    const snapshot = wb?.getSnapshot?.()
    if (snapshot?.styles) stylesLookup = snapshot.styles
    if (snapshot?.sheets?.[sheetId]) sheetSnapshot = snapshot.sheets[sheetId]
  } catch { /* */ }

  const cellDatas: (any | null)[][] = dataRange.getCellDatas() || []

  const totalRows = cellDatas.length

  const newValueState = new Map<string, string>()
  const newStyleState = new Map<string, string>()
  const newFormulaState = new Map<string, string>()
  let lastDataRow = 0
  let lastDataCol = 0

  for (let r = 0; r < totalRows; r++) {
    const row = cellDatas[r]
    if (!row) continue
    const univerRow = r + rangeStartRow  // Actual Univer row index
    for (let c = 0; c < row.length; c++) {
      const cell = row[c]
      const univerCol = c + rangeStartCol  // Actual Univer col index

      const val = cell?.v
      if (val != null && val !== '') {
        if (univerRow > lastDataRow) lastDataRow = univerRow
        if (univerCol > lastDataCol) lastDataCol = univerCol
        // Round numeric values to 10 decimal places to prevent precision drift
        // between HyperFormula (initial load) and Univer (recalculation)
        const strVal = typeof val === 'number'
          ? String(Math.round(val * 1e6) / 1e6)
          : String(val)
        newValueState.set(`${univerRow}:${univerCol}`, strVal)
      }

      // Formula sync — direct mapping, no row offset
      {
        let formula = cell?.f
        if (!formula && cell?.si) {
          try {
            const formulas = sheet.getRange(univerRow, univerCol, 1, 1).getFormulas()
            const resolved = formulas?.[0]?.[0]
            if (resolved && typeof resolved === 'string') formula = resolved
          } catch { /* */ }
        }
        if (formula && typeof formula === 'string') {
          newFormulaState.set(`${univerRow}:${univerCol}`, formula)
        }
      }

      const styleKey = `${univerRow}:${univerCol}`

      // Get style from cell.s only (not getBackgrounds) to avoid capturing
      // CF-computed backgrounds as cell styles. CF overlays are separate.
      const styleRef = cell?.s
      let resolvedStyle: Record<string, any> | null = null
      if (typeof styleRef === 'string' && stylesLookup[styleRef]) {
        resolvedStyle = cleanResolvedStyle(stylesLookup[styleRef])
      } else if (typeof styleRef === 'object' && styleRef !== null) {
        resolvedStyle = cleanResolvedStyle(styleRef)
      }

      if (resolvedStyle && Object.keys(resolvedStyle).length > 0) {
        newStyleState.set(styleKey, stableStringify(resolvedStyle))
      }
    }
  }

  // Column/row data from snapshot
  if (sheetSnapshot) {
    const defaultColWidth = sheetSnapshot.defaultColumnWidth || 88
    const defaultRowHeight = sheetSnapshot.defaultRowHeight || 24

    if (sheetSnapshot.columnData) {
      for (const [colIdx, colData] of Object.entries(sheetSnapshot.columnData as Record<string, any>)) {
        const entry: Record<string, any> = {}
        if (colData.w != null && colData.w !== defaultColWidth) entry.w = colData.w
        if (colData.hd) entry.hd = colData.hd
        if (typeof colData.s === 'string' && stylesLookup[colData.s]) {
          const cleaned = cleanResolvedStyle(stylesLookup[colData.s])
          if (Object.keys(cleaned).length > 0) entry.s = cleaned
        }
        if (Object.keys(entry).length > 0) {
          newStyleState.set(`__col:${colIdx}`, stableStringify(entry))
        }
      }
    }

    if (sheetSnapshot.rowData) {
      for (const [rowIdx, rowDt] of Object.entries(sheetSnapshot.rowData as Record<string, any>)) {
        const entry: Record<string, any> = {}
        if (rowDt.h != null && rowDt.h !== defaultRowHeight) entry.h = rowDt.h
        if (rowDt.hd) entry.hd = rowDt.hd
        if (rowDt.ia != null) entry.ia = rowDt.ia
        if (typeof rowDt.s === 'string' && stylesLookup[rowDt.s]) {
          const cleaned = cleanResolvedStyle(stylesLookup[rowDt.s])
          if (Object.keys(cleaned).length > 0) entry.s = cleaned
        }
        if (Object.keys(entry).length > 0) {
          newStyleState.set(`__row:${rowIdx}`, stableStringify(entry))
        }
      }
    }
  }

  // Value diffing
  const valuesToSet: [string, string][] = []
  const valuesToDelete: string[] = []
  for (const [key, val] of newValueState) {
    if (cellCache.get(key) !== val) valuesToSet.push([key, val])
  }
  for (const key of cellCache.keys()) {
    if (!newValueState.has(key)) valuesToDelete.push(key)
  }
  cellCache.clear()
  for (const [k, v] of newValueState) cellCache.set(k, v)

  // Style diffing
  const stylesToSet: [string, any][] = []
  const stylesToDelete: string[] = []
  for (const [key, val] of newStyleState) {
    if (styleCache.get(key) !== val) stylesToSet.push([key, JSON.parse(val)])
  }
  for (const key of styleCache.keys()) {
    if (!newStyleState.has(key)) stylesToDelete.push(key)
  }
  styleCache.clear()
  for (const [k, v] of newStyleState) styleCache.set(k, v)

  // Formula diffing
  const formulasToSet: [string, string][] = []
  const formulasToDelete: string[] = []
  for (const [key, val] of newFormulaState) {
    if (formulaCache.get(key) !== val) formulasToSet.push([key, val])
  }
  for (const key of formulaCache.keys()) {
    if (!newFormulaState.has(key)) formulasToDelete.push(key)
  }
  formulaCache.clear()
  for (const [k, v] of newFormulaState) formulaCache.set(k, v)

  const hasValueChanges = valuesToSet.length > 0 || valuesToDelete.length > 0
  const hasStyleChanges = stylesToSet.length > 0 || stylesToDelete.length > 0
  const hasFormulaChanges = formulasToSet.length > 0 || formulasToDelete.length > 0


  // Hyperlink diff: compute before early return so hyperlink-only changes are detected
  let hlToRemove: string[] = []
  let hlToSet: [string, { id: string; row: number; col: number; payload: string; display: string }][] = []
  if (s.hyperlinks) {
    const hlMap = s.hyperlinks
    const univerLinks = new Map<string, { id: string; payload: string; display: string }>()
    for (let r = 0; r < totalRows; r++) {
      const row = cellDatas[r]
      if (!row) continue
      const hlUniverRow = r + rangeStartRow
      for (let c = 0; c < row.length; c++) {
        const cell = row[c]
        const hlUniverCol = c + rangeStartCol
        if (!cell?.p?.body?.customRanges) continue
        for (const cr of cell.p.body.customRanges) {
          if (cr.rangeType === 0 /* HYPERLINK */ && cr.properties?.url) {
            const yDocRow = hlUniverRow - 1  // Univer row 0 is header
            if (yDocRow < 0) continue
            const display = cell.p.body.dataStream
              ?.slice(cr.startIndex, cr.endIndex + 1)
              ?.replace(/[\x1e\x1f]/g, '') || ''
            univerLinks.set(`${yDocRow}:${hlUniverCol}`, {
              id: `${cr.rangeId}`,
              payload: cr.properties.url,
              display,
            })
          }
        }
      }
    }
    for (const [key] of hlMap.entries()) {
      if (!univerLinks.has(key)) hlToRemove.push(key)
    }
    for (const [key, link] of univerLinks) {
      const existing = hlMap.get(key) as { payload?: string; display?: string } | undefined
      if (!existing || existing.payload !== link.payload || existing.display !== link.display) {
        const [r, c] = key.split(':').map(Number)
        hlToSet.push([key, { id: link.id, row: r, col: c, payload: link.payload, display: link.display }])
      }
    }
  }
  const hasHyperlinkChanges = hlToRemove.length > 0 || hlToSet.length > 0

  // Cell image diff: detect cells with p.drawingsOrder (inline images)
  let ciToRemove: string[] = []
  let ciToSet: [string, Record<string, unknown>][] = []
  if (s.cellImages) {
    const ciMap = s.cellImages
    const univerCellImages = new Map<string, string>()
    for (let r = 0; r < totalRows; r++) {
      const row = cellDatas[r]
      if (!row) continue
      for (let c = 0; c < row.length; c++) {
        const cell = row[c]
        if (!cell?.p?.drawingsOrder?.length || !cell?.p?.drawings) continue
        univerCellImages.set(`${r}:${c}`, JSON.stringify(cell.p))
      }
    }
    for (const [key] of ciMap.entries()) {
      if (!univerCellImages.has(key)) ciToRemove.push(key)
    }
    for (const [key, pJson] of univerCellImages) {
      const existing = ciMap.get(key) as Record<string, unknown> | undefined
      if (!existing) {
        // New cell image (e.g. user inserted via UI) — store full p
        const [r, c] = key.split(':').map(Number)
        ciToSet.push([key, { row: r, col: c, p: JSON.parse(pJson) }])
      }
      // Don't update existing entries — Univer mutates docTransform.size
      // during rendering, which would cause false dirty state
    }
  }
  const hasCellImageChanges = ciToRemove.length > 0 || ciToSet.length > 0

  // Always check merges
  syncMerges(sheet, s.merges, doc)

  if (!hasValueChanges && !hasStyleChanges && !hasFormulaChanges && !hasHyperlinkChanges && !hasCellImageChanges) return

  dirtySheetIds?.add(sheetId)

  // No header concept — all rows are data. Direct mapping: Univer row r = Y.Doc r:c.
  const dataRows = lastDataRow + 1  // +1 because lastDataRow is 0-indexed
  const dataCols = lastDataCol + 1

  doc.transact(() => {
    // Meta updates (no colNames — removed header concept)
    const oldRows = (s.meta.get('rows') as number) || 0
    const oldCols = (s.meta.get('cols') as number) || 0
    if (oldRows !== dataRows) s.meta.set('rows', dataRows)
    if (oldCols !== dataCols) s.meta.set('cols', dataCols)

    // Cell value changes — direct mapping, no offset
    for (const [key, val] of valuesToSet) {
      s.cells.set(key, val)
    }
    for (const key of valuesToDelete) {
      if (s.cells.has(key)) {
        const parts = key.split(':')
        const r = Number(parts[0])
        const c = Number(parts[1])
        if (r < dataRows && c < dataCols) {
          s.cells.set(key, '')
        } else {
          s.cells.delete(key)
        }
      }
    }
    for (const [yKey] of s.cells.entries()) {
      const parts = yKey.split(':')
      const r = Number(parts[0])
      const c = Number(parts[1])
      if (r >= dataRows || c >= dataCols) {
        s.cells.delete(yKey)
      }
    }

    // Style changes
    for (const [key, style] of stylesToSet) {
      s.styles.set(key, style)
    }
    for (const key of stylesToDelete) {
      if (s.styles.has(key)) s.styles.delete(key)
    }

    // Formula changes
    for (const [key, val] of formulasToSet) {
      s.formulas.set(key, val)
    }
    for (const key of formulasToDelete) {
      if (s.formulas.has(key)) s.formulas.delete(key)
    }

    // Hyperlink changes (pre-computed above)
    if (s.hyperlinks && hasHyperlinkChanges) {
      for (const key of hlToRemove) s.hyperlinks.delete(key)
      for (const [key, link] of hlToSet) s.hyperlinks.set(key, link)
    }

    // Cell image changes (pre-computed above)
    if (s.cellImages && hasCellImageChanges) {
      for (const key of ciToRemove) s.cellImages.delete(key)
      for (const [key, entry] of ciToSet) s.cellImages.set(key, entry)
    }
  }, 'local')
}

/** Sync merge state from Univer to Y.Map('merges') for a specific sheet */
function syncMerges(sheet: any, mergesMap: Y.Map<unknown>, doc: Y.Doc) {
  try {
    const mergedRanges = sheet.getMergeData() as any[]
    if (!mergedRanges) return

    const newMerges: string[] = []
    for (const fRange of mergedRanges) {
      let startRow: number, endRow: number, startCol: number, endCol: number
      try {
        const range = fRange.getRange()
        startRow = range.startRow; endRow = range.endRow
        startCol = range.startColumn; endCol = range.endColumn
      } catch {
        try {
          startRow = fRange.getRow(); startCol = fRange.getColumn()
          endRow = startRow + fRange.getNumRows() - 1
          endCol = startCol + fRange.getNumColumns() - 1
        } catch { continue }
      }
      const dataStartR = startRow - 1
      const dataEndR = endRow - 1
      if (dataStartR < 0) continue
      newMerges.push(`${dataStartR}:${startCol}-${dataEndR}:${endCol}`)
    }
    newMerges.sort()

    const currentMerges: string[] = []
    for (const [, val] of mergesMap.entries()) {
      currentMerges.push(val as string)
    }
    currentMerges.sort()

    if (JSON.stringify(newMerges) !== JSON.stringify(currentMerges)) {
      doc.transact(() => {
        mergesMap.forEach((_, k) => mergesMap.delete(k))
        newMerges.forEach((m, i) => mergesMap.set(String(i), m))
      }, 'local')
    }
  } catch { /* */ }
}
